import { isLegacyActionRecord, isPatchBasedActionRecord, } from "@buildwithflux/models";
import { addElements, deleteSubjects, removeDocumentProperties, removeSubjectProperties, setDocumentProperties, setSubjectProperties, updateDescription, } from "../../document-actions/document/actions";
import { updateNetsLabel } from "../../document-actions/document/net/actions";
/**
 * Maximum number of designators to show in a description before truncating
 */
const MAX_DESIGNATORS_TO_SHOW = 5;
/**
 * Maximum number of properties to show in a description before truncating
 */
const MAX_PROPERTIES_TO_SHOW = 3;
/**
 * Maximum length for truncated description text
 */
const MAX_DESCRIPTION_LENGTH = 60;
/**
 * Maximum length for property values in descriptions
 */
const MAX_PROPERTY_VALUE_LENGTH = 30;
/**
 * Action name constants for actions defined in frontend (not in core package).
 * These are used to avoid hardcoding strings throughout the code.
 */
const FRONTEND_ACTION_NAMES = {
    updateName: "updateName",
    addSubjectsFromClipboard: "addSubjectsFromClipboard",
    setGlobalPcbRuleSet: "setGlobalPcbRuleSet",
    removeGlobalPcbRuleSet: "removeGlobalPcbRuleSet",
    toggleGlobalPcbRule: "toggleGlobalPcbRule",
    updateWiring: "updateWiring",
    rotateElements: "rotateElements",
    flipElements: "flipElements",
    lockObjects: "lockObjects",
    setCode: "setCode",
    revertDocumentVersion: "revertDocumentVersion",
    restoreDocumentVersion: "restoreDocumentVersion",
    convertSubjectsToNewPart: "convertSubjectsToNewPart",
    updateElementsSchematicPosition: "updateElementsSchematicPosition",
    // Additional action types for dynamic descriptions
    updateElementsLabel: "updateElementsLabel",
    updateSimulationTimeStep: "updateSimulationTimeStep",
    updateSimulationTimeStepUnit: "updateSimulationTimeStepUnit",
    updatePropertyVisibility: "updatePropertyVisibility",
    addSubjectsPropertiesFromClipboard: "addSubjectsPropertiesFromClipboard",
    duplicateGlobalPcbRuleSet: "duplicateGlobalPcbRuleSet",
    addGlobalRulesFromClipboard: "addGlobalRulesFromClipboard",
    setAssets: "setAssets",
    removeAssets: "removeAssets",
    addNewElement: "addNewElement",
    addNewRoute: "addNewRoute",
    addRoutes: "addRoutes",
    addBranchPoint: "addBranchPoint",
    updateElementsPosition: "updateElementsPosition",
    // Action types for PCB node operations
    removeNodes: "removeNodes",
    setNodeRules: "setNodeRules",
    removeNodeRules: "removeNodeRules",
    toggleNodeSpecificRule: "toggleNodeSpecificRule",
    // AI-generated content actions
    rewrittenProjectProperties: "rewrittenProjectProperties",
    rewrittenProjectDescription: "rewrittenProjectDescription",
    // Undo/redo and auto-layout actions
    undoDocument: "undoDocument",
    applyAutoLayoutIteration: "applyAutoLayoutIteration",
    // Markdown document actions
    createProjectDoc: "createProjectDoc",
    updateProjectDoc: "updateProjectDoc",
    deleteProjectDoc: "deleteProjectDoc",
};
/**
 * Action names that support dynamic descriptions.
 * Uses action creator .type properties to follow DRY principles.
 * For actions defined in frontend, we use string constants.
 */
const SUPPORTED_ACTIONS = [
    deleteSubjects.type,
    addElements.type,
    setSubjectProperties.type,
    removeSubjectProperties.type,
    setDocumentProperties.type,
    removeDocumentProperties.type,
    updateDescription.type,
    FRONTEND_ACTION_NAMES.createProjectDoc,
    FRONTEND_ACTION_NAMES.updateProjectDoc,
    FRONTEND_ACTION_NAMES.deleteProjectDoc,
    FRONTEND_ACTION_NAMES.updateName,
    FRONTEND_ACTION_NAMES.addSubjectsFromClipboard,
    FRONTEND_ACTION_NAMES.setGlobalPcbRuleSet,
    FRONTEND_ACTION_NAMES.removeGlobalPcbRuleSet,
    FRONTEND_ACTION_NAMES.toggleGlobalPcbRule,
    FRONTEND_ACTION_NAMES.updateWiring,
    FRONTEND_ACTION_NAMES.rotateElements,
    FRONTEND_ACTION_NAMES.flipElements,
    FRONTEND_ACTION_NAMES.lockObjects,
    FRONTEND_ACTION_NAMES.setCode,
    FRONTEND_ACTION_NAMES.revertDocumentVersion,
    FRONTEND_ACTION_NAMES.restoreDocumentVersion,
    FRONTEND_ACTION_NAMES.convertSubjectsToNewPart,
    FRONTEND_ACTION_NAMES.updateElementsSchematicPosition,
    // Additional action types
    FRONTEND_ACTION_NAMES.updateElementsLabel,
    FRONTEND_ACTION_NAMES.updateSimulationTimeStep,
    FRONTEND_ACTION_NAMES.updateSimulationTimeStepUnit,
    FRONTEND_ACTION_NAMES.updatePropertyVisibility,
    FRONTEND_ACTION_NAMES.addSubjectsPropertiesFromClipboard,
    FRONTEND_ACTION_NAMES.duplicateGlobalPcbRuleSet,
    FRONTEND_ACTION_NAMES.addGlobalRulesFromClipboard,
    FRONTEND_ACTION_NAMES.setAssets,
    FRONTEND_ACTION_NAMES.removeAssets,
    FRONTEND_ACTION_NAMES.addNewElement,
    FRONTEND_ACTION_NAMES.addNewRoute,
    FRONTEND_ACTION_NAMES.addRoutes,
    FRONTEND_ACTION_NAMES.addBranchPoint,
    FRONTEND_ACTION_NAMES.updateElementsPosition,
    updateNetsLabel.type,
    // PCB node operations
    FRONTEND_ACTION_NAMES.removeNodes,
    FRONTEND_ACTION_NAMES.setNodeRules,
    FRONTEND_ACTION_NAMES.removeNodeRules,
    FRONTEND_ACTION_NAMES.toggleNodeSpecificRule,
    // AI-generated content actions
    FRONTEND_ACTION_NAMES.rewrittenProjectProperties,
    FRONTEND_ACTION_NAMES.rewrittenProjectDescription,
    // Undo/redo and auto-layout actions
    FRONTEND_ACTION_NAMES.undoDocument,
    FRONTEND_ACTION_NAMES.applyAutoLayoutIteration,
];
/**
 * Check if an action name supports dynamic descriptions
 */
export function supportsDynamicDescription(actionName) {
    return SUPPORTED_ACTIONS.includes(actionName);
}
/**
 * Extract affected element UIDs from an action record's change diff.
 *
 * For patch-based records, looks for paths like ["elements", "uid", ...] where the operation
 * indicates addition or removal.
 *
 * For legacy deep-diff records, looks for paths like ["elements", "uid"] with kind "N" (new)
 * or "D" (deleted).
 */
function extractAffectedElementUids(actionRecord, actionType) {
    const elementUids = [];
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            // Check if this patch affects elements
            if (patch.path[0] === "elements" && patch.path.length >= 2) {
                const uid = patch.path[1];
                // For additions, look for "add" operations at the element level
                // For removals, look for "remove" operations at the element level
                if (actionType === "add" && patch.op === "add" && patch.path.length === 2) {
                    elementUids.push(uid);
                }
                else if (actionType === "remove" && patch.op === "remove" && patch.path.length === 2) {
                    elementUids.push(uid);
                }
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            // Check if this diff affects elements
            if (diff.path && diff.path[0] === "elements" && diff.path.length >= 2) {
                const uid = diff.path[1];
                // For additions, look for "N" (new) kind
                // For removals, look for "D" (deleted) kind
                if (actionType === "add" && diff.kind === "N" && diff.path.length === 2) {
                    elementUids.push(uid);
                }
                else if (actionType === "remove" && diff.kind === "D" && diff.path.length === 2) {
                    elementUids.push(uid);
                }
            }
        }
    }
    return elementUids;
}
/**
 * Extract the label of an added element from the action record's change diff.
 * This extracts the label from the forward patches/diffs so we don't need to rely
 * on the current document state (which may have changed since the action was recorded).
 */
function extractAddedElementLabel(actionRecord, elementUid) {
    if (isPatchBasedActionRecord(actionRecord)) {
        // For patch-based records, the added element data is in the forward patches
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            if (patch.path[0] === "elements" &&
                patch.path[1] === elementUid &&
                patch.path.length === 2 &&
                patch.op === "add") {
                // The forward patch adds the element, so the value contains the element data
                const elementData = patch.value;
                return elementData?.label;
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            if (diff.path &&
                diff.path[0] === "elements" &&
                diff.path[1] === elementUid &&
                diff.path.length === 2 &&
                diff.kind === "N") {
                // For new items, rhs contains the new value
                const elementData = diff.rhs;
                return elementData?.label;
            }
        }
    }
    return undefined;
}
/**
 * Get designators (labels) for a list of element UIDs from the action record.
 *
 * For both added and removed elements, we extract labels from the action record's
 * change diff to ensure we get the label as it was at the time of the action,
 * not the current document state which may have changed.
 */
function getDesignatorsForElements(elementUids, actionRecord, actionType) {
    const designators = [];
    for (const uid of elementUids) {
        let label;
        if (actionType === "add") {
            // For added elements, extract the label from the forward patches
            label = extractAddedElementLabel(actionRecord, uid);
        }
        else {
            // For removed elements, extract the label from the reverse patches
            label = extractRemovedElementLabel(actionRecord, uid);
        }
        if (label && label.trim() !== "") {
            designators.push(label);
        }
    }
    return designators;
}
/**
 * Extract affected net UIDs from an action record's change diff.
 *
 * For patch-based records, looks for paths like ["nets", "uid", ...] where the operation
 * indicates addition or removal.
 *
 * For legacy deep-diff records, looks for paths like ["nets", "uid"] with kind "N" (new)
 * or "D" (deleted).
 *
 * Note: Nets are stored under the "nets" key in the document, not "routes".
 * The "routes" key was used in older versions but "nets" is the current format.
 */
function extractAffectedNetUids(actionRecord, actionType) {
    const netUids = [];
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            // Check if this patch affects nets (check both "nets" and legacy "routes" keys)
            if ((patch.path[0] === "nets" || patch.path[0] === "routes") && patch.path.length >= 2) {
                const uid = patch.path[1];
                // For additions, look for "add" operations at the net level
                // For removals, look for "remove" operations at the net level
                if (actionType === "add" && patch.op === "add" && patch.path.length === 2) {
                    netUids.push(uid);
                }
                else if (actionType === "remove" && patch.op === "remove" && patch.path.length === 2) {
                    netUids.push(uid);
                }
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            // Check if this diff affects nets (check both "nets" and legacy "routes" keys)
            if (diff.path && (diff.path[0] === "nets" || diff.path[0] === "routes") && diff.path.length >= 2) {
                const uid = diff.path[1];
                // For additions, look for "N" (new) kind
                // For removals, look for "D" (deleted) kind
                if (actionType === "add" && diff.kind === "N" && diff.path.length === 2) {
                    netUids.push(uid);
                }
                else if (actionType === "remove" && diff.kind === "D" && diff.path.length === 2) {
                    netUids.push(uid);
                }
            }
        }
    }
    return netUids;
}
/**
 * Extract the label of a removed net from the action record's change diff.
 * For deletions, the label is in the reverse patches (patch-based) or lhs (legacy).
 *
 * Note: Nets are stored under the "nets" key in the document, not "routes".
 * The "routes" key was used in older versions but "nets" is the current format.
 */
function extractRemovedNetLabel(actionRecord, netUid) {
    if (isPatchBasedActionRecord(actionRecord)) {
        // For patch-based records, the removed net data is in the reverse patches
        const patches = actionRecord.actionChangeDiff.reverse;
        for (const patch of patches) {
            // Check both "nets" and legacy "routes" keys
            if ((patch.path[0] === "nets" || patch.path[0] === "routes") &&
                patch.path[1] === netUid &&
                patch.path.length === 2 &&
                patch.op === "add") {
                // The reverse patch adds back the net, so the value contains the net data
                const netData = patch.value;
                return netData?.label;
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            // Check both "nets" and legacy "routes" keys
            if (diff.path &&
                (diff.path[0] === "nets" || diff.path[0] === "routes") &&
                diff.path[1] === netUid &&
                diff.path.length === 2 &&
                diff.kind === "D") {
                // For deleted items, lhs contains the original value
                const netData = diff.lhs;
                return netData?.label;
            }
        }
    }
    return undefined;
}
/**
 * Get designators (labels) for a list of net UIDs from the action record.
 * For removed nets, we extract labels from the reverse patches or lhs.
 */
function getDesignatorsForNets(netUids, actionRecord) {
    const designators = [];
    for (const uid of netUids) {
        const label = extractRemovedNetLabel(actionRecord, uid);
        if (label) {
            designators.push(label);
        }
    }
    return designators;
}
/**
 * Extract the label of a removed element from the action record's change diff.
 */
function extractRemovedElementLabel(actionRecord, elementUid) {
    if (isPatchBasedActionRecord(actionRecord)) {
        // For patch-based records, the removed element data is in the reverse patches
        // or we can look at the value in the forward "remove" patch
        const patches = actionRecord.actionChangeDiff.reverse;
        for (const patch of patches) {
            if (patch.path[0] === "elements" &&
                patch.path[1] === elementUid &&
                patch.path.length === 2 &&
                patch.op === "add") {
                // The reverse patch adds back the element, so the value contains the element data
                const elementData = patch.value;
                return elementData?.label;
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            if (diff.path &&
                diff.path[0] === "elements" &&
                diff.path[1] === elementUid &&
                diff.path.length === 2 &&
                diff.kind === "D") {
                // For deleted items, lhs contains the original value
                const elementData = diff.lhs;
                return elementData?.label;
            }
        }
    }
    return undefined;
}
/**
 * Truncate a string to a maximum length, adding ellipsis if needed.
 */
function truncateText(text, maxLength = MAX_DESCRIPTION_LENGTH) {
    if (!text)
        return "";
    const trimmed = text.trim().replace(/\n/g, " ");
    if (trimmed.length <= maxLength) {
        return trimmed;
    }
    return trimmed.substring(0, maxLength - 3) + "...";
}
/**
 * Count the number of affected subjects from property-related action records.
 * For setSubjectProperties and removeSubjectProperties, the patches affect
 * paths like ["subjects", subjectUid, "properties", ...] or similar structures.
 */
function countAffectedSubjects(actionRecord) {
    const subjectUids = new Set();
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            // Look for paths that affect subjects or elements with properties
            if (patch.path.length >= 2) {
                const rootKey = patch.path[0];
                if (rootKey === "subjects" || rootKey === "elements") {
                    subjectUids.add(patch.path[1]);
                }
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            if (diff.path && diff.path.length >= 2) {
                const rootKey = diff.path[0];
                if (rootKey === "subjects" || rootKey === "elements") {
                    subjectUids.add(diff.path[1]);
                }
            }
        }
    }
    return subjectUids.size;
}
/**
 * Count the number of affected document properties from document property action records.
 */
function countAffectedDocumentProperties(actionRecord) {
    const propertyUids = new Set();
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            // Look for paths that affect document properties
            if (patch.path[0] === "properties" && patch.path.length >= 2) {
                propertyUids.add(patch.path[1]);
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            if (diff.path && diff.path[0] === "properties" && diff.path.length >= 2) {
                propertyUids.add(diff.path[1]);
            }
        }
    }
    return propertyUids.size;
}
/**
 * Extract the old and new description text from an updateDescription action record.
 */
function extractDescriptionChange(actionRecord) {
    if (isPatchBasedActionRecord(actionRecord)) {
        const forwardPatches = actionRecord.actionChangeDiff.forward;
        const reversePatches = actionRecord.actionChangeDiff.reverse;
        let newValue;
        let oldValue;
        for (const patch of forwardPatches) {
            if (patch.path[0] === "description" && patch.path.length === 1) {
                if (typeof patch.value === "string") {
                    newValue = patch.value;
                }
            }
        }
        // Get old value from reverse patches
        for (const patch of reversePatches) {
            if (patch.path[0] === "description" && patch.path.length === 1) {
                if (typeof patch.value === "string") {
                    oldValue = patch.value;
                }
            }
        }
        if (newValue !== undefined || oldValue !== undefined) {
            return { oldValue, newValue };
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            if (diff.path && diff.path[0] === "description" && diff.path.length === 1) {
                if (diff.kind === "E") {
                    // Edit: has both old and new values
                    return {
                        oldValue: typeof diff.lhs === "string" ? diff.lhs : undefined,
                        newValue: typeof diff.rhs === "string" ? diff.rhs : undefined,
                    };
                }
                else if (diff.kind === "N" && typeof diff.rhs === "string") {
                    // New: only has new value
                    return { newValue: diff.rhs };
                }
            }
        }
    }
    return undefined;
}
/**
 * Extract the old and new name from an updateName action record.
 * The name is stored in the "name" field of the document.
 */
function extractNameChange(actionRecord) {
    if (isPatchBasedActionRecord(actionRecord)) {
        const forwardPatches = actionRecord.actionChangeDiff.forward;
        const reversePatches = actionRecord.actionChangeDiff.reverse;
        let newValue;
        let oldValue;
        for (const patch of forwardPatches) {
            if (patch.path[0] === "name" && patch.path.length === 1) {
                if (typeof patch.value === "string") {
                    newValue = patch.value;
                }
            }
        }
        // Get old value from reverse patches
        for (const patch of reversePatches) {
            if (patch.path[0] === "name" && patch.path.length === 1) {
                if (typeof patch.value === "string") {
                    oldValue = patch.value;
                }
            }
        }
        if (newValue !== undefined || oldValue !== undefined) {
            return { oldValue, newValue };
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            if (diff.path && diff.path[0] === "name" && diff.path.length === 1) {
                if (diff.kind === "E") {
                    // Edit: has both old and new values
                    return {
                        oldValue: typeof diff.lhs === "string" ? diff.lhs : undefined,
                        newValue: typeof diff.rhs === "string" ? diff.rhs : undefined,
                    };
                }
                else if (diff.kind === "N" && typeof diff.rhs === "string") {
                    // New: only has new value
                    return { newValue: diff.rhs };
                }
            }
        }
    }
    return undefined;
}
/**
 * Format a property value for display, truncating if necessary.
 * Optionally appends the unit if provided.
 */
function formatPropertyValue(value, unit) {
    if (value === undefined || value === null) {
        return "";
    }
    if (typeof value === "object") {
        // For object values (like vectors or links), just show a simplified representation
        if ("type" in value) {
            const typedValue = value;
            if (typedValue.type === "link") {
                return "[link]";
            }
            if (typedValue.type === "vector2" || typedValue.type === "vector3") {
                return "[position]";
            }
        }
        return "[object]";
    }
    const strValue = String(value);
    const truncatedValue = truncateText(strValue, MAX_PROPERTY_VALUE_LENGTH);
    // Append unit if available and value doesn't already include it
    if (unit && !strValue.includes(unit)) {
        return `${truncatedValue}${unit}`;
    }
    return truncatedValue;
}
/**
 * Extract document property changes from an action record.
 */
function extractDocumentPropertyChanges(actionRecord, changeType) {
    const changes = [];
    const seenPropertyIds = new Set();
    if (isPatchBasedActionRecord(actionRecord)) {
        const forwardPatches = actionRecord.actionChangeDiff.forward;
        const reversePatches = actionRecord.actionChangeDiff.reverse;
        for (const patch of forwardPatches) {
            if (patch.path[0] === "properties" && patch.path.length >= 2) {
                const propertyId = patch.path[1];
                if (seenPropertyIds.has(propertyId))
                    continue;
                seenPropertyIds.add(propertyId);
                if (changeType === "set" && (patch.op === "add" || patch.op === "replace")) {
                    // For set operations, extract the property data
                    const propertyData = patch.value;
                    const name = propertyData?.name;
                    const newValue = propertyData?.value;
                    // Try to find old value from reverse patches
                    let oldValue;
                    for (const reversePatch of reversePatches) {
                        if (reversePatch.path[0] === "properties" &&
                            reversePatch.path[1] === propertyId &&
                            reversePatch.path.length === 2) {
                            const oldPropertyData = reversePatch.value;
                            oldValue = oldPropertyData?.value;
                            break;
                        }
                    }
                    changes.push({
                        propertyId,
                        name,
                        oldValue,
                        newValue,
                        unit: propertyData?.unit,
                        changeType: oldValue !== undefined ? "update" : "add",
                    });
                }
                else if (changeType === "remove" && patch.op === "remove") {
                    // For remove operations, get the property name from reverse patches
                    let name;
                    let oldValue;
                    for (const reversePatch of reversePatches) {
                        if (reversePatch.path[0] === "properties" &&
                            reversePatch.path[1] === propertyId &&
                            reversePatch.path.length === 2) {
                            const oldPropertyData = reversePatch.value;
                            name = oldPropertyData?.name;
                            oldValue = oldPropertyData?.value;
                            break;
                        }
                    }
                    changes.push({
                        propertyId,
                        name,
                        oldValue,
                        changeType: "remove",
                    });
                }
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            if (diff.path && diff.path[0] === "properties" && diff.path.length >= 2) {
                const propertyId = diff.path[1];
                if (seenPropertyIds.has(propertyId))
                    continue;
                seenPropertyIds.add(propertyId);
                if (changeType === "set") {
                    if (diff.kind === "N") {
                        // New property
                        const propertyData = diff.rhs;
                        changes.push({
                            propertyId,
                            name: propertyData?.name,
                            newValue: propertyData?.value,
                            unit: propertyData?.unit,
                            changeType: "add",
                        });
                    }
                    else if (diff.kind === "E") {
                        // Edited property - this might be a nested change
                        // Check if it's a value change
                        if (diff.path.length >= 3 && diff.path[2] === "value") {
                            changes.push({
                                propertyId,
                                oldValue: diff.lhs,
                                newValue: diff.rhs,
                                changeType: "update",
                            });
                        }
                    }
                }
                else if (changeType === "remove" && diff.kind === "D") {
                    const propertyData = diff.lhs;
                    changes.push({
                        propertyId,
                        name: propertyData?.name,
                        oldValue: propertyData?.value,
                        changeType: "remove",
                    });
                }
            }
        }
    }
    return changes;
}
/**
 * Extract subject property changes from an action record.
 * Returns a map of subject UIDs to their property changes.
 */
function extractSubjectPropertyChanges(actionRecord, changeType) {
    const subjectChanges = new Map();
    if (isPatchBasedActionRecord(actionRecord)) {
        const forwardPatches = actionRecord.actionChangeDiff.forward;
        const reversePatches = actionRecord.actionChangeDiff.reverse;
        for (const patch of forwardPatches) {
            // Look for paths like ["elements", elementUid, "properties", propertyUid, ...]
            // or ["subjects", subjectUid, "properties", propertyUid, ...]
            const rootKey = patch.path[0];
            if ((rootKey === "elements" || rootKey === "subjects") && patch.path.length >= 4) {
                const subjectId = patch.path[1];
                if (patch.path[2] !== "properties")
                    continue;
                const propertyId = patch.path[3];
                if (!subjectChanges.has(subjectId)) {
                    subjectChanges.set(subjectId, []);
                }
                const changes = subjectChanges.get(subjectId);
                // Check if we already have this property
                if (changes.some((c) => c.propertyId === propertyId))
                    continue;
                if (changeType === "set" && (patch.op === "add" || patch.op === "replace")) {
                    const propertyData = patch.value;
                    const name = propertyData?.name;
                    const newValue = propertyData?.value;
                    const unit = propertyData?.unit;
                    // Try to find old value from reverse patches
                    let oldValue;
                    for (const reversePatch of reversePatches) {
                        if (reversePatch.path[0] === rootKey &&
                            reversePatch.path[1] === subjectId &&
                            reversePatch.path[2] === "properties" &&
                            reversePatch.path[3] === propertyId &&
                            reversePatch.path.length === 4) {
                            const oldPropertyData = reversePatch.value;
                            oldValue = oldPropertyData?.value;
                            break;
                        }
                    }
                    changes.push({
                        propertyId,
                        name,
                        oldValue,
                        newValue,
                        unit,
                        changeType: oldValue !== undefined ? "update" : "add",
                    });
                }
                else if (changeType === "remove" && patch.op === "remove") {
                    let name;
                    let oldValue;
                    for (const reversePatch of reversePatches) {
                        if (reversePatch.path[0] === rootKey &&
                            reversePatch.path[1] === subjectId &&
                            reversePatch.path[2] === "properties" &&
                            reversePatch.path[3] === propertyId &&
                            reversePatch.path.length === 4) {
                            const oldPropertyData = reversePatch.value;
                            name = oldPropertyData?.name;
                            oldValue = oldPropertyData?.value;
                            break;
                        }
                    }
                    changes.push({
                        propertyId,
                        name,
                        oldValue,
                        changeType: "remove",
                    });
                }
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            if (!diff.path || diff.path.length < 4)
                continue;
            const rootKey = diff.path[0];
            if (rootKey !== "elements" && rootKey !== "subjects")
                continue;
            if (diff.path[2] !== "properties")
                continue;
            const subjectId = diff.path[1];
            const propertyId = diff.path[3];
            if (!subjectChanges.has(subjectId)) {
                subjectChanges.set(subjectId, []);
            }
            const changes = subjectChanges.get(subjectId);
            if (changes.some((c) => c.propertyId === propertyId))
                continue;
            if (changeType === "set") {
                if (diff.kind === "N") {
                    const propertyData = diff.rhs;
                    changes.push({
                        propertyId,
                        name: propertyData?.name,
                        newValue: propertyData?.value,
                        unit: propertyData?.unit,
                        changeType: "add",
                    });
                }
                else if (diff.kind === "E") {
                    // Check if it's a value change
                    if (diff.path.length >= 5 && diff.path[4] === "value") {
                        changes.push({
                            propertyId,
                            oldValue: diff.lhs,
                            newValue: diff.rhs,
                            changeType: "update",
                        });
                    }
                }
            }
            else if (changeType === "remove" && diff.kind === "D") {
                const propertyData = diff.lhs;
                changes.push({
                    propertyId,
                    name: propertyData?.name,
                    oldValue: propertyData?.value,
                    unit: propertyData?.unit,
                    changeType: "remove",
                });
            }
        }
    }
    return subjectChanges;
}
/**
 * Format property changes into a human-readable string.
 */
function formatPropertyChanges(changes, showValues = true) {
    if (changes.length === 0) {
        return "";
    }
    const formattedItems = [];
    for (const change of changes.slice(0, MAX_PROPERTIES_TO_SHOW)) {
        const name = change.name ?? change.propertyId;
        if (showValues && change.newValue !== undefined) {
            const valueStr = formatPropertyValue(change.newValue, change.unit);
            if (valueStr) {
                formattedItems.push(`${name} = ${valueStr}`);
            }
            else {
                formattedItems.push(name);
            }
        }
        else {
            formattedItems.push(name);
        }
    }
    if (changes.length > MAX_PROPERTIES_TO_SHOW) {
        const remaining = changes.length - MAX_PROPERTIES_TO_SHOW;
        formattedItems.push(`${remaining} more`);
    }
    if (formattedItems.length === 1) {
        return formattedItems[0];
    }
    if (formattedItems.length === 2) {
        return `${formattedItems[0]} and ${formattedItems[1]}`;
    }
    const allButLast = formattedItems.slice(0, -1).join(", ");
    const last = formattedItems[formattedItems.length - 1];
    return `${allButLast}, and ${last}`;
}
/**
 * Extract PCB rule set changes from an action record's change diff.
 * Parses patches that affect pcbLayoutRuleSets to determine what changed.
 */
function extractPcbRuleSetChanges(actionRecord) {
    const changes = new Map();
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            // Check if this patch affects pcbLayoutRuleSets
            if (patch.path[0] === "pcbLayoutRuleSets" && patch.path.length >= 2) {
                const ruleSetUid = patch.path[1];
                // Initialize change entry if not exists
                if (!changes.has(ruleSetUid)) {
                    changes.set(ruleSetUid, {
                        ruleSetUid,
                        isNew: false,
                        rulesAdded: [],
                        rulesRemoved: [],
                        rulesUpdated: [],
                    });
                }
                const change = changes.get(ruleSetUid);
                // Adding a new rule set
                if (patch.op === "add" && patch.path.length === 2) {
                    change.isNew = true;
                    const ruleSet = patch.value;
                    if (ruleSet.name) {
                        change.ruleSetName = ruleSet.name;
                    }
                    if (ruleSet.selector) {
                        change.selector = ruleSet.selector;
                    }
                }
                // Updating rule set name
                else if (patch.path[2] === "name" && patch.path.length === 3) {
                    if (patch.op === "replace" || patch.op === "add") {
                        change.ruleSetName = patch.value;
                    }
                }
                // Updating selector
                else if (patch.path[2] === "selector" && patch.path.length === 3) {
                    if (patch.op === "replace") {
                        change.selector = patch.value;
                    }
                    else if (patch.op === "add") {
                        change.selector = patch.value;
                    }
                }
                // Changes to rules within the rule set
                else if (patch.path[2] === "rules" && patch.path.length >= 4) {
                    const ruleKey = patch.path[3];
                    if (patch.op === "add" && patch.path.length === 4) {
                        // New rule added
                        change.rulesAdded.push(ruleKey);
                    }
                    else if (patch.op === "remove" && patch.path.length === 4) {
                        // Rule removed
                        change.rulesRemoved.push(ruleKey);
                    }
                    else if (patch.path[4] === "value" && patch.path.length === 5) {
                        // Rule value updated
                        const newValue = patch.value;
                        change.rulesUpdated.push({
                            key: ruleKey,
                            newValue: newValue !== undefined ? String(newValue) : undefined,
                        });
                    }
                }
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        // Handle legacy deep-diff format
        const diffs = actionRecord.actionChangeDiff;
        for (const diff of diffs) {
            if (diff.path && diff.path[0] === "pcbLayoutRuleSets" && diff.path.length >= 2) {
                const ruleSetUid = diff.path[1];
                if (!changes.has(ruleSetUid)) {
                    changes.set(ruleSetUid, {
                        ruleSetUid,
                        isNew: false,
                        rulesAdded: [],
                        rulesRemoved: [],
                        rulesUpdated: [],
                    });
                }
                const change = changes.get(ruleSetUid);
                // New rule set (kind "N" = new)
                if (diff.kind === "N" && diff.path.length === 2) {
                    change.isNew = true;
                    const ruleSet = diff.rhs;
                    if (ruleSet?.name) {
                        change.ruleSetName = ruleSet.name;
                    }
                    if (ruleSet?.selector) {
                        change.selector = ruleSet.selector;
                    }
                }
                // Selector change (kind "E" = edit)
                else if (diff.kind === "E" && diff.path[2] === "selector") {
                    change.selector = diff.rhs;
                    change.oldSelector = diff.lhs;
                }
                // Rule changes
                else if (diff.path[2] === "rules" && diff.path.length >= 4) {
                    const ruleKey = diff.path[3];
                    if (diff.kind === "N") {
                        change.rulesAdded.push(ruleKey);
                    }
                    else if (diff.kind === "D") {
                        change.rulesRemoved.push(ruleKey);
                    }
                    else if (diff.kind === "E" && diff.path[4] === "value") {
                        change.rulesUpdated.push({
                            key: ruleKey,
                            oldValue: diff.lhs !== undefined ? String(diff.lhs) : undefined,
                            newValue: diff.rhs !== undefined ? String(diff.rhs) : undefined,
                        });
                    }
                }
            }
        }
    }
    return Array.from(changes.values());
}
/**
 * Extract removed PCB rule set UIDs from an action record.
 * For removeGlobalPcbRuleSet actions, looks for remove operations on pcbLayoutRuleSets.
 */
function extractRemovedPcbRuleSetUids(actionRecord) {
    const removedUids = [];
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            if (patch.path[0] === "pcbLayoutRuleSets" && patch.path.length === 2 && patch.op === "remove") {
                removedUids.push(patch.path[1]);
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        const diffs = actionRecord.actionChangeDiff;
        for (const diff of diffs) {
            if (diff.path && diff.path[0] === "pcbLayoutRuleSets" && diff.path.length === 2 && diff.kind === "D") {
                removedUids.push(diff.path[1]);
            }
        }
    }
    return removedUids;
}
/**
 * Extract toggle state change from a toggleGlobalPcbRule action record.
 * Returns the rule set UID and whether it was enabled or disabled.
 */
function extractPcbRuleToggleChange(actionRecord) {
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            // Look for disabled field changes on pcbLayoutRuleSets
            if (patch.path[0] === "pcbLayoutRuleSets" && patch.path.length === 3 && patch.path[2] === "disabled") {
                const ruleSetUid = patch.path[1];
                const disabled = patch.value;
                return { ruleSetUid, enabled: !disabled };
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        const diffs = actionRecord.actionChangeDiff;
        for (const diff of diffs) {
            if (diff.kind === "E" &&
                diff.path &&
                diff.path[0] === "pcbLayoutRuleSets" &&
                diff.path.length === 3 &&
                diff.path[2] === "disabled") {
                const ruleSetUid = diff.path[1];
                const disabled = typeof diff.rhs === "boolean" ? diff.rhs : false;
                return { ruleSetUid, enabled: !disabled };
            }
        }
    }
    return undefined;
}
/**
 * Check if an action record only affects wiring (routeVertices) without removing elements or nets.
 * This is used to detect wire segment deletions that should show "updated wiring" instead of "deleted subjects".
 */
function hasWiringOnlyChanges(actionRecord) {
    let hasRouteVertexChanges = false;
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            // Check if this patch affects routeVertices (wire geometry)
            if (patch.path[0] === "routeVertices") {
                hasRouteVertexChanges = true;
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        const diffs = actionRecord.actionChangeDiff;
        for (const diff of diffs) {
            // Check if this diff affects routeVertices
            if (diff.path && diff.path[0] === "routeVertices") {
                hasRouteVertexChanges = true;
            }
        }
    }
    return hasRouteVertexChanges;
}
/**
 * Extract PCB layout node changes from an action record.
 * Returns the count of unique PCB layout nodes affected.
 * This is used to detect position/layout changes that don't involve element deletions.
 */
function extractPcbLayoutNodeChanges(actionRecord) {
    const affectedNodeUids = new Set();
    let hasRemovals = false;
    let hasAdditions = false;
    let hasUpdates = false;
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            if (patch.path[0] === "pcbLayoutNodes" && patch.path.length >= 2) {
                const nodeUid = patch.path[1];
                affectedNodeUids.add(nodeUid);
                if (patch.op === "remove" && patch.path.length === 2) {
                    hasRemovals = true;
                }
                else if (patch.op === "add" && patch.path.length === 2) {
                    hasAdditions = true;
                }
                else {
                    hasUpdates = true;
                }
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        const diffs = actionRecord.actionChangeDiff;
        for (const diff of diffs) {
            if (diff.path && diff.path[0] === "pcbLayoutNodes" && diff.path.length >= 2) {
                const nodeUid = diff.path[1];
                affectedNodeUids.add(nodeUid);
                if (diff.kind === "D" && diff.path.length === 2) {
                    hasRemovals = true;
                }
                else if (diff.kind === "N" && diff.path.length === 2) {
                    hasAdditions = true;
                }
                else {
                    hasUpdates = true;
                }
            }
        }
    }
    return {
        affectedNodeCount: affectedNodeUids.size,
        hasRemovals,
        hasAdditions,
        hasUpdates,
    };
}
/**
 * Extract element update changes from an action record.
 * Returns the count of unique elements that were updated (replace operations) without being removed,
 * and categorizes the type of change (position, symbol_placement, property, or other).
 * This is used to detect cases where deleteSubjects is used for element property updates,
 * not actual deletions.
 */
function extractElementUpdateChanges(actionRecord) {
    const updatedElementUids = new Set();
    let hasUpdates = false;
    let hasPositionChanges = false;
    let hasSymbolPlacementChanges = false;
    let hasPropertyChanges = false;
    let hasOtherChanges = false;
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            if (patch.path[0] === "elements" && patch.path.length >= 2) {
                const elementUid = patch.path[1];
                if (patch.op === "replace") {
                    updatedElementUids.add(elementUid);
                    hasUpdates = true;
                    // Categorize the type of change based on the path
                    if (patch.path.length >= 3) {
                        const fieldName = patch.path[2];
                        if (fieldName === "diagram_position" || fieldName === "position") {
                            hasPositionChanges = true;
                        }
                        else if (fieldName === "symbol_placements") {
                            hasSymbolPlacementChanges = true;
                        }
                        else if (fieldName === "properties") {
                            hasPropertyChanges = true;
                        }
                        else {
                            hasOtherChanges = true;
                        }
                    }
                    else {
                        hasOtherChanges = true;
                    }
                }
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        const diffs = actionRecord.actionChangeDiff;
        for (const diff of diffs) {
            if (diff.path && diff.path[0] === "elements" && diff.path.length >= 2) {
                const elementUid = diff.path[1];
                if (diff.kind === "E") {
                    updatedElementUids.add(elementUid);
                    hasUpdates = true;
                    // Categorize the type of change based on the path
                    if (diff.path.length >= 3) {
                        const fieldName = diff.path[2];
                        if (fieldName === "diagram_position" || fieldName === "position") {
                            hasPositionChanges = true;
                        }
                        else if (fieldName === "symbol_placements") {
                            hasSymbolPlacementChanges = true;
                        }
                        else if (fieldName === "properties") {
                            hasPropertyChanges = true;
                        }
                        else {
                            hasOtherChanges = true;
                        }
                    }
                    else {
                        hasOtherChanges = true;
                    }
                }
            }
        }
    }
    // Determine the primary change type (prioritize position > symbol_placement > property > other)
    let changeType = "other";
    if (hasPositionChanges) {
        changeType = "position";
    }
    else if (hasSymbolPlacementChanges) {
        changeType = "symbol_placement";
    }
    else if (hasPropertyChanges) {
        changeType = "property";
    }
    return {
        updatedElementCount: updatedElementUids.size,
        hasUpdates,
        changeType,
    };
}
/**
 * Extract wiring changes from an updateWiring action record.
 * The updateWiring action can modify:
 * - routes: net metadata including labels (designators)
 * - routeVertices: wire geometry (positions, connections)
 *
 * We look at both to provide the most informative description.
 */
function extractWiringChanges(actionRecord) {
    const netChanges = new Map();
    let hasRouteChanges = false;
    let hasVertexChanges = false;
    const affectedVertices = new Set();
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            // Check if this patch affects nets (check both "nets" and legacy "routes" keys)
            if ((patch.path[0] === "nets" || patch.path[0] === "routes") && patch.path.length >= 2) {
                hasRouteChanges = true;
                const netUid = patch.path[1];
                if (!netChanges.has(netUid)) {
                    netChanges.set(netUid, {
                        netUid,
                        isNew: false,
                        isUpdated: false,
                        isRemoved: false,
                    });
                }
                const change = netChanges.get(netUid);
                // Removing a net (net merge case - one net is consumed into another)
                if (patch.op === "remove" && patch.path.length === 2) {
                    change.isRemoved = true;
                    // Try to get the label from the reverse patch
                    const reversePatches = actionRecord.actionChangeDiff.reverse;
                    for (const reversePatch of reversePatches) {
                        if (reversePatch.op === "add" &&
                            (reversePatch.path[0] === "nets" || reversePatch.path[0] === "routes") &&
                            reversePatch.path[1] === netUid &&
                            reversePatch.path.length === 2) {
                            const net = reversePatch.value;
                            if (net.label) {
                                change.label = net.label;
                            }
                            break;
                        }
                    }
                }
                // Adding a new net
                else if (patch.op === "add" && patch.path.length === 2) {
                    change.isNew = true;
                    const net = patch.value;
                    if (net.label) {
                        change.label = net.label;
                    }
                }
                // Updating net label
                else if (patch.path[2] === "label" && patch.path.length === 3) {
                    if (patch.op === "replace" || patch.op === "add") {
                        change.label = patch.value;
                        if (!change.isNew) {
                            change.isUpdated = true;
                        }
                    }
                }
                // Any other update to an existing net
                else if (patch.path.length > 2 && !change.isNew) {
                    change.isUpdated = true;
                }
            }
            // Check if this patch affects routeVertices (wire geometry)
            else if (patch.path[0] === "routeVertices" && patch.path.length >= 2) {
                hasVertexChanges = true;
                const vertexUid = patch.path[1];
                affectedVertices.add(vertexUid);
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        const diffs = actionRecord.actionChangeDiff;
        for (const diff of diffs) {
            // Check if this diff affects nets (check both "nets" and legacy "routes" keys)
            if (diff.path && (diff.path[0] === "nets" || diff.path[0] === "routes") && diff.path.length >= 2) {
                hasRouteChanges = true;
                const netUid = diff.path[1];
                if (!netChanges.has(netUid)) {
                    netChanges.set(netUid, {
                        netUid,
                        isNew: false,
                        isUpdated: false,
                        isRemoved: false,
                    });
                }
                const change = netChanges.get(netUid);
                // Removing a net (net merge case - one net is consumed into another)
                // In legacy diffs, "D" means deleted
                if (diff.kind === "D" && diff.path.length === 2) {
                    change.isRemoved = true;
                    // The label is in diff.lhs (the old value before deletion)
                    const net = diff.lhs;
                    if (net?.label) {
                        change.label = net.label;
                    }
                }
                // Adding a new net
                else if (diff.kind === "N" && diff.path.length === 2) {
                    change.isNew = true;
                    const net = diff.rhs;
                    if (net?.label) {
                        change.label = net.label;
                    }
                }
                else if (diff.kind === "E" && diff.path[2] === "label") {
                    change.label = diff.rhs;
                    if (!change.isNew) {
                        change.isUpdated = true;
                    }
                }
                else if (diff.kind === "E" && diff.path.length > 2 && !change.isNew) {
                    change.isUpdated = true;
                }
            }
            else if (diff.path && diff.path[0] === "routeVertices" && diff.path.length >= 2) {
                hasVertexChanges = true;
                const vertexUid = diff.path[1];
                affectedVertices.add(vertexUid);
            }
        }
    }
    return {
        hasRouteChanges,
        hasVertexChanges,
        netChanges: Array.from(netChanges.values()),
        vertexCount: affectedVertices.size,
    };
}
/**
 * Extract simulation time step value change from an action record.
 */
function extractSimulationTimeStepChange(actionRecord) {
    if (isPatchBasedActionRecord(actionRecord)) {
        const forwardPatches = actionRecord.actionChangeDiff.forward;
        const reversePatches = actionRecord.actionChangeDiff.reverse;
        let newValue;
        let oldValue;
        for (const patch of forwardPatches) {
            if (patch.path[0] === "simulationTimeStepSize" && patch.path.length === 1) {
                if (typeof patch.value === "number") {
                    newValue = patch.value;
                }
            }
        }
        for (const patch of reversePatches) {
            if (patch.path[0] === "simulationTimeStepSize" && patch.path.length === 1) {
                if (typeof patch.value === "number") {
                    oldValue = patch.value;
                }
            }
        }
        if (newValue !== undefined || oldValue !== undefined) {
            return { oldValue, newValue };
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            if (diff.path && diff.path[0] === "simulationTimeStepSize" && diff.path.length === 1) {
                if (diff.kind === "E") {
                    return {
                        oldValue: typeof diff.lhs === "number" ? diff.lhs : undefined,
                        newValue: typeof diff.rhs === "number" ? diff.rhs : undefined,
                    };
                }
                else if (diff.kind === "N" && typeof diff.rhs === "number") {
                    return { newValue: diff.rhs };
                }
            }
        }
    }
    return undefined;
}
/**
 * Extract simulation time step unit change from an action record.
 */
function extractSimulationTimeStepUnitChange(actionRecord) {
    if (isPatchBasedActionRecord(actionRecord)) {
        const forwardPatches = actionRecord.actionChangeDiff.forward;
        const reversePatches = actionRecord.actionChangeDiff.reverse;
        let newValue;
        let oldValue;
        for (const patch of forwardPatches) {
            if (patch.path[0] === "simulationTimeStepSizeUnit" && patch.path.length === 1) {
                if (typeof patch.value === "string") {
                    newValue = patch.value;
                }
            }
        }
        for (const patch of reversePatches) {
            if (patch.path[0] === "simulationTimeStepSizeUnit" && patch.path.length === 1) {
                if (typeof patch.value === "string") {
                    oldValue = patch.value;
                }
            }
        }
        if (newValue !== undefined || oldValue !== undefined) {
            return { oldValue, newValue };
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            if (diff.path && diff.path[0] === "simulationTimeStepSizeUnit" && diff.path.length === 1) {
                if (diff.kind === "E") {
                    return {
                        oldValue: typeof diff.lhs === "string" ? diff.lhs : undefined,
                        newValue: typeof diff.rhs === "string" ? diff.rhs : undefined,
                    };
                }
                else if (diff.kind === "N" && typeof diff.rhs === "string") {
                    return { newValue: diff.rhs };
                }
            }
        }
    }
    return undefined;
}
/**
 * Extract element label changes from an updateElementsLabel action record.
 */
function extractElementLabelChanges(actionRecord) {
    const changes = [];
    if (isPatchBasedActionRecord(actionRecord)) {
        const forwardPatches = actionRecord.actionChangeDiff.forward;
        const reversePatches = actionRecord.actionChangeDiff.reverse;
        // Map to collect changes per element
        const elementChanges = new Map();
        for (const patch of forwardPatches) {
            if (patch.path[0] === "elements" && patch.path.length >= 3 && patch.path[2] === "label") {
                const elementUid = patch.path[1];
                const existing = elementChanges.get(elementUid) || {};
                existing.newLabel = typeof patch.value === "string" ? patch.value : undefined;
                elementChanges.set(elementUid, existing);
            }
        }
        for (const patch of reversePatches) {
            if (patch.path[0] === "elements" && patch.path.length >= 3 && patch.path[2] === "label") {
                const elementUid = patch.path[1];
                const existing = elementChanges.get(elementUid) || {};
                existing.oldLabel = typeof patch.value === "string" ? patch.value : undefined;
                elementChanges.set(elementUid, existing);
            }
        }
        for (const [elementUid, change] of elementChanges) {
            changes.push({ elementUid, ...change });
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            if (diff.path && diff.path[0] === "elements" && diff.path.length >= 3 && diff.path[2] === "label") {
                const elementUid = diff.path[1];
                if (diff.kind === "E") {
                    changes.push({
                        elementUid,
                        oldLabel: typeof diff.lhs === "string" ? diff.lhs : undefined,
                        newLabel: typeof diff.rhs === "string" ? diff.rhs : undefined,
                    });
                }
                else if (diff.kind === "N" && typeof diff.rhs === "string") {
                    changes.push({ elementUid, newLabel: diff.rhs });
                }
            }
        }
    }
    return changes;
}
/**
 * Extract net label changes from an updateNetsLabel action record.
 * Returns old and new label values for each net that was updated.
 */
function extractNetLabelChanges(actionRecord) {
    const changes = [];
    if (isPatchBasedActionRecord(actionRecord)) {
        const forwardPatches = actionRecord.actionChangeDiff.forward;
        const reversePatches = actionRecord.actionChangeDiff.reverse;
        // Map to collect changes per net
        const netChanges = new Map();
        for (const patch of forwardPatches) {
            // Check for nets.{netUid}.label changes (check both "nets" and legacy "routes" keys)
            if ((patch.path[0] === "nets" || patch.path[0] === "routes") &&
                patch.path.length >= 3 &&
                patch.path[2] === "label") {
                const netUid = patch.path[1];
                const existing = netChanges.get(netUid) || {};
                existing.newLabel = typeof patch.value === "string" ? patch.value : undefined;
                netChanges.set(netUid, existing);
            }
        }
        for (const patch of reversePatches) {
            if ((patch.path[0] === "nets" || patch.path[0] === "routes") &&
                patch.path.length >= 3 &&
                patch.path[2] === "label") {
                const netUid = patch.path[1];
                const existing = netChanges.get(netUid) || {};
                existing.oldLabel = typeof patch.value === "string" ? patch.value : undefined;
                netChanges.set(netUid, existing);
            }
        }
        for (const [netUid, change] of netChanges) {
            changes.push({ netUid, ...change });
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            if (diff.path &&
                (diff.path[0] === "nets" || diff.path[0] === "routes") &&
                diff.path.length >= 3 &&
                diff.path[2] === "label") {
                const netUid = diff.path[1];
                if (diff.kind === "E") {
                    changes.push({
                        netUid,
                        oldLabel: typeof diff.lhs === "string" ? diff.lhs : undefined,
                        newLabel: typeof diff.rhs === "string" ? diff.rhs : undefined,
                    });
                }
                else if (diff.kind === "N" && typeof diff.rhs === "string") {
                    changes.push({ netUid, newLabel: diff.rhs });
                }
            }
        }
    }
    return changes;
}
/**
 * Extract property visibility change from an updatePropertyVisibility action record.
 */
function extractPropertyVisibilityChange(actionRecord) {
    let propertyId;
    let propertyName;
    let visibility;
    const elementUids = new Set();
    let isMetamodulePath = false;
    const propertyUidsFromMetamodule = new Set();
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            // Look for paths like ["elements", elementUid, "properties", propertyUid, "visible"]
            if (patch.path[0] === "elements" &&
                patch.path.length >= 5 &&
                patch.path[2] === "properties" &&
                patch.path[4] === "visible") {
                elementUids.add(patch.path[1]);
                propertyId = patch.path[3];
                if (typeof patch.value === "boolean") {
                    visibility = patch.value;
                }
            }
            // Also look for metamodule.visibilityToggleMap paths:
            // ["elements", elementUid, "metamodule", "visibilityToggleMap", propertyUid]
            else if (patch.path[0] === "elements" &&
                patch.path.length >= 5 &&
                patch.path[2] === "metamodule" &&
                patch.path[3] === "visibilityToggleMap") {
                elementUids.add(patch.path[1]);
                isMetamodulePath = true;
                // Capture the property UID from the path
                propertyUidsFromMetamodule.add(patch.path[4]);
                if (typeof patch.value === "boolean") {
                    visibility = patch.value;
                }
            }
            // Handle metamodule addition: ["elements", elementUid, "metamodule"]
            // with value containing visibilityToggleMap
            else if (patch.path[0] === "elements" &&
                patch.path.length === 3 &&
                patch.path[2] === "metamodule" &&
                patch.value &&
                typeof patch.value === "object" &&
                "visibilityToggleMap" in patch.value) {
                elementUids.add(patch.path[1]);
                isMetamodulePath = true;
                // Extract property UIDs from the visibilityToggleMap
                const toggleMap = patch.value.visibilityToggleMap;
                for (const propUid of Object.keys(toggleMap)) {
                    propertyUidsFromMetamodule.add(propUid);
                }
            }
        }
        // Try to find property names from the action record's patches
        // Look for property patches that contain the name field
        if (propertyUidsFromMetamodule.size > 0) {
            for (const patch of patches) {
                // Look for property patches: ["elements", elementUid, "properties", propertyUid]
                // or ["elements", elementUid, "properties", propertyUid, "name"]
                if (patch.path[0] === "elements" && patch.path[2] === "properties" && patch.path.length >= 4) {
                    const patchPropertyUid = patch.path[3];
                    if (propertyUidsFromMetamodule.has(patchPropertyUid)) {
                        // Check if the value contains a name
                        if (patch.value && typeof patch.value === "object" && "name" in patch.value) {
                            const name = patch.value.name;
                            if (name) {
                                propertyName = name;
                                break;
                            }
                        }
                    }
                }
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            if (diff.path &&
                diff.path[0] === "elements" &&
                diff.path.length >= 5 &&
                diff.path[2] === "properties" &&
                diff.path[4] === "visible") {
                elementUids.add(diff.path[1]);
                propertyId = diff.path[3];
                if (diff.kind === "E" && typeof diff.rhs === "boolean") {
                    visibility = diff.rhs;
                }
            }
            // Also look for metamodule.visibilityToggleMap paths in legacy format
            else if (diff.path &&
                diff.path[0] === "elements" &&
                diff.path.length >= 5 &&
                diff.path[2] === "metamodule" &&
                diff.path[3] === "visibilityToggleMap") {
                elementUids.add(diff.path[1]);
                isMetamodulePath = true;
                propertyUidsFromMetamodule.add(diff.path[4]);
                if (diff.kind === "E" && typeof diff.rhs === "boolean") {
                    visibility = diff.rhs;
                }
            }
        }
    }
    if (elementUids.size > 0) {
        return { propertyId, propertyName, visibility, elementCount: elementUids.size, isMetamodulePath };
    }
    return undefined;
}
/**
 * Extract asset changes from setAssets or removeAssets action records.
 */
function extractAssetChanges(actionRecord, actionType) {
    const assetNames = [];
    const assetUids = new Set();
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionType === "add" ? actionRecord.actionChangeDiff.forward : actionRecord.actionChangeDiff.reverse;
        for (const patch of patches) {
            if (patch.path[0] === "assets" && patch.path.length >= 2) {
                const assetUid = patch.path[1];
                assetUids.add(assetUid);
                // Try to extract asset name from the value
                if (patch.op === "add" && patch.path.length === 2) {
                    const assetData = patch.value;
                    if (assetData?.name) {
                        assetNames.push(assetData.name);
                    }
                }
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            if (diff.path && diff.path[0] === "assets" && diff.path.length >= 2) {
                const assetUid = diff.path[1];
                assetUids.add(assetUid);
                // Try to extract asset name
                if (actionType === "add" && diff.kind === "N" && diff.path.length === 2) {
                    const assetData = diff.rhs;
                    if (assetData?.name) {
                        assetNames.push(assetData.name);
                    }
                }
                else if (actionType === "remove" && diff.kind === "D" && diff.path.length === 2) {
                    const assetData = diff.lhs;
                    if (assetData?.name) {
                        assetNames.push(assetData.name);
                    }
                }
            }
        }
    }
    return { assetNames, assetCount: assetUids.size };
}
/**
 * Extract added net label from an action record (for addNewRoute, addRoutes actions).
 *
 * Note: Nets are stored under the "nets" key in the document, not "routes".
 * The "routes" key was used in older versions but "nets" is the current format.
 */
function extractAddedNetLabel(actionRecord, netUid) {
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            // Check both "nets" and legacy "routes" keys
            if ((patch.path[0] === "nets" || patch.path[0] === "routes") &&
                patch.path[1] === netUid &&
                patch.path.length === 2 &&
                patch.op === "add") {
                const netData = patch.value;
                return netData?.label;
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        for (const diff of actionRecord.actionChangeDiff) {
            // Check both "nets" and legacy "routes" keys
            if (diff.path &&
                (diff.path[0] === "nets" || diff.path[0] === "routes") &&
                diff.path[1] === netUid &&
                diff.path.length === 2 &&
                diff.kind === "N") {
                const netData = diff.rhs;
                return netData?.label;
            }
        }
    }
    return undefined;
}
/**
 * Get designators (labels) for added nets from the action record.
 */
function getDesignatorsForAddedNets(netUids, actionRecord) {
    const designators = [];
    for (const uid of netUids) {
        const label = extractAddedNetLabel(actionRecord, uid);
        if (label) {
            designators.push(label);
        }
    }
    return designators;
}
/**
 * Format a list of designators into a human-readable string.
 *
 * Examples:
 * - ["R1"] -> "R1"
 * - ["R1", "R2"] -> "R1 and R2"
 * - ["R1", "R2", "C1"] -> "R1, R2, and C1"
 * - ["R1", "R2", "C1", "C2", "C3", "C4"] -> "R1, R2, C1, C2, C3, and 1 more"
 */
/**
 * Extract lock state from a lockObjects action record.
 * Returns true if locking, false if unlocking.
 */
function extractLockState(actionRecord) {
    if (isPatchBasedActionRecord(actionRecord)) {
        const patches = actionRecord.actionChangeDiff.forward;
        for (const patch of patches) {
            // Look for locked property changes
            if (patch.path.includes("locked") && (patch.op === "replace" || patch.op === "add")) {
                return patch.value === true;
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        const diffs = actionRecord.actionChangeDiff;
        for (const diff of diffs) {
            if (diff.path && diff.path.includes("locked") && diff.kind === "E") {
                return diff.rhs === true;
            }
        }
    }
    // Default to locking if we can't determine
    return true;
}
/**
 * Detect the type of transform from patch paths in position update actions.
 * This analyzes the patch paths to determine if the action is a move, rotation, flip, or combination.
 *
 * Patch paths for position updates typically look like:
 * - ["elements", "uid", "symbol_placements", "suffix", "x"] - x position
 * - ["elements", "uid", "symbol_placements", "suffix", "y"] - y position
 * - ["elements", "uid", "symbol_placements", "suffix", "orientation"] - rotation
 * - ["elements", "uid", "symbol_placements", "suffix", "flip"] - flip
 * - ["elements", "uid", "symbol_placements", "suffix", "mirror"] - mirror
 * - ["elements", "uid", "diagram_position", "x"] - legacy x position
 * - ["elements", "uid", "diagram_position", "y"] - legacy y position
 * - ["elements", "uid", "diagram_position", "orientation"] - legacy rotation
 */
/**
 * Helper to recursively compare two values and detect transform-related changes.
 * Returns flags indicating what types of changes were detected.
 */
function compareValuesForTransformChanges(newVal, oldVal) {
    let hasPositionChange = false;
    let hasOrientationChange = false;
    let hasFlipChange = false;
    // If both are objects, compare their properties recursively
    if (newVal && oldVal && typeof newVal === "object" && typeof oldVal === "object") {
        const newObj = newVal;
        const oldObj = oldVal;
        // Check all keys in both objects
        const allKeys = new Set([...Object.keys(newObj), ...Object.keys(oldObj)]);
        for (const key of allKeys) {
            const newFieldVal = newObj[key];
            const oldFieldVal = oldObj[key];
            // Check for position changes (x, y)
            if (key === "x" || key === "y") {
                if (newFieldVal !== oldFieldVal) {
                    hasPositionChange = true;
                }
            }
            // Check for orientation changes
            else if (key === "orientation") {
                if (newFieldVal !== oldFieldVal) {
                    hasOrientationChange = true;
                }
            }
            // Check for flip/mirror changes
            else if (key === "flip" || key === "mirror") {
                if (newFieldVal !== oldFieldVal) {
                    hasFlipChange = true;
                }
            }
            // Recursively check nested objects (like symbol placements with symbol names as keys)
            else if (newFieldVal && oldFieldVal && typeof newFieldVal === "object" && typeof oldFieldVal === "object") {
                const nested = compareValuesForTransformChanges(newFieldVal, oldFieldVal);
                hasPositionChange = hasPositionChange || nested.hasPositionChange;
                hasOrientationChange = hasOrientationChange || nested.hasOrientationChange;
                hasFlipChange = hasFlipChange || nested.hasFlipChange;
            }
        }
    }
    return { hasPositionChange, hasOrientationChange, hasFlipChange };
}
function detectTransformType(actionRecord) {
    let hasPositionChange = false;
    let hasOrientationChange = false;
    let hasFlipChange = false;
    if (isPatchBasedActionRecord(actionRecord)) {
        const forwardPatches = actionRecord.actionChangeDiff.forward;
        const reversePatches = actionRecord.actionChangeDiff.reverse;
        for (const forwardPatch of forwardPatches) {
            if (!forwardPatch || forwardPatch.path[0] !== "elements" || forwardPatch.path.length < 2) {
                continue;
            }
            const pathStr = forwardPatch.path.join(".");
            const lastField = forwardPatch.path[forwardPatch.path.length - 1];
            // First, check for individual field patches (legacy behavior)
            if (lastField === "x" || lastField === "y") {
                hasPositionChange = true;
            }
            else if (lastField === "orientation") {
                hasOrientationChange = true;
            }
            else if (lastField === "flip" || lastField === "mirror") {
                hasFlipChange = true;
            }
            // For object-level patches (symbol_placements, diagram_position, position),
            // compare forward vs reverse values to detect actual changes
            else if (lastField === "symbol_placements" ||
                lastField === "diagram_position" ||
                lastField === "position") {
                // Find the corresponding reverse patch with the same path
                const reversePatch = reversePatches.find((rp) => rp && rp.path.join(".") === pathStr);
                if (reversePatch && forwardPatch.value !== undefined && reversePatch.value !== undefined) {
                    const changes = compareValuesForTransformChanges(forwardPatch.value, reversePatch.value);
                    hasPositionChange = hasPositionChange || changes.hasPositionChange;
                    hasOrientationChange = hasOrientationChange || changes.hasOrientationChange;
                    hasFlipChange = hasFlipChange || changes.hasFlipChange;
                }
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        const diffs = actionRecord.actionChangeDiff;
        for (const diff of diffs) {
            if (diff.path && diff.path[0] === "elements" && diff.path.length >= 2) {
                const lastField = diff.path[diff.path.length - 1];
                if (lastField === "x" || lastField === "y") {
                    hasPositionChange = true;
                }
                if (lastField === "orientation") {
                    hasOrientationChange = true;
                }
                if (lastField === "flip" || lastField === "mirror") {
                    hasFlipChange = true;
                }
            }
        }
    }
    // Determine the transform type based on what we found
    if (hasFlipChange) {
        return "flip";
    }
    if (hasOrientationChange && hasPositionChange) {
        return "move_and_rotate";
    }
    if (hasOrientationChange) {
        return "rotation";
    }
    if (hasPositionChange) {
        return "move";
    }
    return "unknown";
}
function formatDesignatorList(designators) {
    if (designators.length === 0) {
        return "";
    }
    if (designators.length === 1) {
        return designators[0];
    }
    if (designators.length === 2) {
        return `${designators[0]} and ${designators[1]}`;
    }
    if (designators.length <= MAX_DESIGNATORS_TO_SHOW) {
        const allButLast = designators.slice(0, -1).join(", ");
        const last = designators[designators.length - 1];
        return `${allButLast}, and ${last}`;
    }
    // Truncate if too many
    const shown = designators.slice(0, MAX_DESIGNATORS_TO_SHOW);
    const remaining = designators.length - MAX_DESIGNATORS_TO_SHOW;
    const shownStr = shown.join(", ");
    return `${shownStr}, and ${remaining} more`;
}
/**
 * Extract element UIDs and their labels from transform action patches.
 * For transform actions (rotate, flip, lock, move), elements are modified in place
 * with "replace" operations on properties like diagram_position, symbol_placements, locked, etc.
 * We need to look at the reverse patches to find the element labels since the forward patches
 * only contain the changed property values, not the full element data.
 */
function extractTransformElementDesignators(actionRecord) {
    const elementUids = new Set();
    const uidToLabel = new Map();
    if (isPatchBasedActionRecord(actionRecord)) {
        // First, collect all affected element UIDs from forward patches
        const forwardPatches = actionRecord.actionChangeDiff.forward;
        for (const patch of forwardPatches) {
            if (patch.path[0] === "elements" && patch.path.length >= 2) {
                elementUids.add(patch.path[1]);
            }
        }
        // Now look in reverse patches for element labels
        // The reverse patches may contain the full element data if the element was modified
        const reversePatches = actionRecord.actionChangeDiff.reverse;
        for (const patch of reversePatches) {
            if (patch.path[0] === "elements" && patch.path.length >= 2) {
                const uid = patch.path[1];
                if (elementUids.has(uid) && !uidToLabel.has(uid)) {
                    // Check if this is a full element (add or replace with path.length === 2)
                    if (patch.path.length === 2 && (patch.op === "add" || patch.op === "replace")) {
                        const elementData = patch.value;
                        if (elementData?.label) {
                            uidToLabel.set(uid, elementData.label);
                        }
                    }
                    // Check if this is a label-specific patch
                    if (patch.path.length === 3 && patch.path[2] === "label") {
                        const label = patch.value;
                        if (label) {
                            uidToLabel.set(uid, label);
                        }
                    }
                }
            }
        }
        // For elements we didn't find labels for, check if the forward patches have element data
        for (const patch of forwardPatches) {
            if (patch.path[0] === "elements" && patch.path.length >= 2) {
                const uid = patch.path[1];
                if (elementUids.has(uid) && !uidToLabel.has(uid)) {
                    // Check if this is a full element (add or replace with path.length === 2)
                    if (patch.path.length === 2 && (patch.op === "add" || patch.op === "replace")) {
                        const elementData = patch.value;
                        if (elementData?.label) {
                            uidToLabel.set(uid, elementData.label);
                        }
                    }
                    // Check if this is a label-specific patch
                    if (patch.path.length === 3 && patch.path[2] === "label") {
                        const label = patch.value;
                        if (label) {
                            uidToLabel.set(uid, label);
                        }
                    }
                }
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        // For legacy records, collect element UIDs and look for labels
        for (const diff of actionRecord.actionChangeDiff) {
            if (diff.path && diff.path[0] === "elements" && diff.path.length >= 2) {
                const uid = diff.path[1];
                elementUids.add(uid);
                // Check if this diff has element data with a label
                if (diff.path.length === 2) {
                    if (diff.kind === "N" && diff.rhs) {
                        const elementData = diff.rhs;
                        if (elementData?.label) {
                            uidToLabel.set(uid, elementData.label);
                        }
                    }
                    else if (diff.kind === "D" && diff.lhs) {
                        const elementData = diff.lhs;
                        if (elementData?.label) {
                            uidToLabel.set(uid, elementData.label);
                        }
                    }
                    else if (diff.kind === "E") {
                        // For edited elements, check both lhs and rhs
                        const lhsData = diff.lhs;
                        const rhsData = diff.rhs;
                        if (lhsData?.label) {
                            uidToLabel.set(uid, lhsData.label);
                        }
                        else if (rhsData?.label) {
                            uidToLabel.set(uid, rhsData.label);
                        }
                    }
                }
                // Check if this is a label-specific diff
                if (diff.path.length === 3 && diff.path[2] === "label") {
                    if (diff.kind === "E") {
                        // For edited labels, prefer rhs (new value) but fall back to lhs (old value)
                        const rhsLabel = diff.rhs;
                        const lhsLabel = diff.lhs;
                        const label = rhsLabel ?? lhsLabel;
                        if (label) {
                            uidToLabel.set(uid, label);
                        }
                    }
                    else if (diff.kind === "N") {
                        // For new labels, only rhs exists
                        const label = diff.rhs;
                        if (label) {
                            uidToLabel.set(uid, label);
                        }
                    }
                }
            }
        }
    }
    return Array.from(uidToLabel.values());
}
/**
 * Count the number of PCB nodes removed by an action.
 * Looks for remove operations on pcbLayoutNodes in the forward patches.
 */
function countRemovedPcbNodes(actionRecord) {
    if (!isPatchBasedActionRecord(actionRecord)) {
        return 0;
    }
    const removedNodeUids = new Set();
    for (const patch of actionRecord.actionChangeDiff.forward) {
        if (patch.op === "remove" && patch.path[0] === "pcbLayoutNodes" && patch.path.length >= 2) {
            const nodeUid = String(patch.path[1]);
            removedNodeUids.add(nodeUid);
        }
    }
    return removedNodeUids.size;
}
/**
 * Extract node rule changes from an action record.
 * For setNodeRules and removeNodeRules actions, extracts the rule names and affected node count.
 */
function extractNodeRuleChanges(actionRecord, mode) {
    if (!isPatchBasedActionRecord(actionRecord)) {
        return { ruleNames: [], nodeCount: 0 };
    }
    const ruleNamesSet = new Set();
    const nodeUidsSet = new Set();
    for (const patch of actionRecord.actionChangeDiff.forward) {
        // Path pattern: ["pcbLayoutNodes", nodeUid, "pcbNodeRuleSet", ruleKey, ...]
        if (patch.path[0] !== "pcbLayoutNodes" || patch.path[2] !== "pcbNodeRuleSet" || patch.path.length < 4) {
            continue;
        }
        const nodeUid = String(patch.path[1]);
        const ruleKey = String(patch.path[3]);
        if (mode === "set" && (patch.op === "replace" || patch.op === "add")) {
            nodeUidsSet.add(nodeUid);
            ruleNamesSet.add(ruleKey);
        }
        else if (mode === "remove" && patch.op === "remove") {
            nodeUidsSet.add(nodeUid);
            ruleNamesSet.add(ruleKey);
        }
    }
    return {
        ruleNames: Array.from(ruleNamesSet),
        nodeCount: nodeUidsSet.size,
    };
}
/**
 * Extract the toggle state of a node-specific rule from an action record.
 * For toggleNodeSpecificRule actions, extracts the rule name and whether it was disabled or enabled.
 */
function extractNodeRuleToggleState(actionRecord) {
    if (!isPatchBasedActionRecord(actionRecord)) {
        return { disabled: false };
    }
    for (const patch of actionRecord.actionChangeDiff.forward) {
        // Path pattern: ["pcbLayoutNodes", nodeUid, "pcbNodeRuleSet", ruleKey, "disabled"]
        if (patch.path[0] === "pcbLayoutNodes" && patch.path[2] === "pcbNodeRuleSet" && patch.path[4] === "disabled") {
            const ruleName = String(patch.path[3]);
            if (patch.op === "add" || patch.op === "replace") {
                return { ruleName, disabled: Boolean(patch.value) };
            }
            if (patch.op === "remove") {
                // Removing the disabled flag means enabling the rule
                return { ruleName, disabled: false };
            }
        }
    }
    return { disabled: false };
}
/**
 * Extract property change counts from a rewrittenProjectProperties action.
 * Reuses the existing extractDocumentPropertyChanges helper.
 */
function extractRewrittenProjectPropertyChanges(actionRecord) {
    const setChanges = extractDocumentPropertyChanges(actionRecord, "set");
    const removedChanges = extractDocumentPropertyChanges(actionRecord, "remove");
    return {
        addedCount: setChanges.length,
        removedCount: removedChanges.length,
    };
}
/**
 * Extract flip layer changes from pcbLayoutNodes in an action record.
 * For flipElements actions that only update pcbLayoutNodes (not elements),
 * this extracts the count of affected nodes and the target layer.
 */
function extractFlipLayerChanges(actionRecord) {
    const nodeUids = new Set();
    let targetLayer;
    const layers = new Set();
    if (isPatchBasedActionRecord(actionRecord)) {
        for (const patch of actionRecord.actionChangeDiff.forward) {
            // Look for paths like ["pcbLayoutNodes", nodeUid, "pcbNodeRuleSet"]
            // with value containing layer property
            if (patch.path[0] === "pcbLayoutNodes" &&
                patch.path.length >= 3 &&
                patch.path[2] === "pcbNodeRuleSet" &&
                (patch.op === "replace" || patch.op === "add")) {
                const nodeUid = String(patch.path[1]);
                nodeUids.add(nodeUid);
                // Extract layer from the value
                if (patch.value && typeof patch.value === "object" && "layer" in patch.value) {
                    const layer = patch.value.layer;
                    if (layer) {
                        layers.add(layer);
                    }
                }
            }
        }
    }
    // If all patches have the same layer, use that as the target layer
    if (layers.size === 1) {
        targetLayer = Array.from(layers)[0];
    }
    return { nodeCount: nodeUids.size, targetLayer };
}
/**
 * Extract removed PCB rule set names from an action record.
 * For removeGlobalPcbRuleSet actions, looks for remove operations on pcbLayoutRuleSets
 * and extracts the rule set names from the reverse patches.
 */
function extractRemovedPcbRuleSetNames(actionRecord) {
    const names = [];
    if (isPatchBasedActionRecord(actionRecord)) {
        // Look in reverse patches for the rule set data (which contains the name)
        const reversePatches = actionRecord.actionChangeDiff.reverse;
        for (const patch of reversePatches) {
            if (patch.path[0] === "pcbLayoutRuleSets" && patch.path.length === 2 && patch.op === "add") {
                const ruleSetData = patch.value;
                if (ruleSetData?.name) {
                    names.push(ruleSetData.name);
                }
                else if (ruleSetData?.selector) {
                    names.push(ruleSetData.selector);
                }
            }
        }
    }
    else if (isLegacyActionRecord(actionRecord)) {
        const diffs = actionRecord.actionChangeDiff;
        for (const diff of diffs) {
            if (diff.path && diff.path[0] === "pcbLayoutRuleSets" && diff.path.length === 2 && diff.kind === "D") {
                const ruleSetData = diff.lhs;
                if (ruleSetData?.name) {
                    names.push(ruleSetData.name);
                }
                else if (ruleSetData?.selector) {
                    names.push(ruleSetData.selector);
                }
            }
        }
    }
    return names;
}
/**
 * Extract markdown doc info (name) from a markdown doc action record.
 * Markdown actions use a legacy diff structure where the path is ["markdownDocs", projectDocUid].
 */
function extractMarkdownDocInfo(actionRecord) {
    // Markdown actions use legacy diffs (arrays) exclusively
    if (Array.isArray(actionRecord.actionChangeDiff)) {
        for (const diff of actionRecord.actionChangeDiff) {
            // Path is ["markdownDocs", projectDocUid]
            if (diff.path && diff.path[0] === "markdownDocs" && diff.path.length === 2) {
                const uid = diff.path[1];
                // For create (N) and update (E), we want the new state (rhs)
                if ((diff.kind === "N" || diff.kind === "E") && diff.rhs) {
                    const doc = diff.rhs;
                    return { uid, name: doc.name || "Untitled Document" };
                }
                // For delete (D), we want the old state (lhs)
                if (diff.kind === "D" && diff.lhs) {
                    const doc = diff.lhs;
                    return { uid, name: doc.name || "Untitled Document" };
                }
            }
        }
    }
    return undefined;
}
/**
 * Generate a dynamic description for an action record based on the affected elements.
 * All element labels are extracted from the action record itself, not from the current
 * document state, ensuring accurate descriptions even for historical actions.
 *
 * @param actionRecord - The action record to generate a description for
 * @returns A human-readable description, or undefined if the action doesn't support
 *          dynamic descriptions or no meaningful description could be generated
 */
export function generateActionDescription(actionRecord) {
    const actionName = actionRecord.actionName;
    if (!supportsDynamicDescription(actionName)) {
        return undefined;
    }
    switch (actionName) {
        case deleteSubjects.type: {
            // Extract both element and net UIDs (both removed and added)
            const removedElementUids = extractAffectedElementUids(actionRecord, "remove");
            const removedNetUids = extractAffectedNetUids(actionRecord, "remove");
            const addedNetUids = extractAffectedNetUids(actionRecord, "add");
            // If no elements or nets were removed, check for added nets (wire split case)
            // or wiring-only changes
            if (removedElementUids.length === 0 && removedNetUids.length === 0) {
                // Check if nets were added (e.g., wire deletion that splits a net)
                if (addedNetUids.length > 0) {
                    const addedNetDesignators = getDesignatorsForAddedNets(addedNetUids, actionRecord);
                    if (addedNetDesignators.length === 1) {
                        // Single net: "removed a wire and split a net, creating Net 4"
                        return `removed a wire and split a net, creating ${addedNetDesignators[0]}`;
                    }
                    if (addedNetDesignators.length > 1) {
                        // Multiple nets: "removed wiring, creating nets Net 4 and Net 5"
                        return `removed wiring, creating nets ${formatDesignatorList(addedNetDesignators)}`;
                    }
                    // Fall back to count if no labels found
                    const count = addedNetUids.length;
                    if (count === 1) {
                        return "removed a wire and split a net, creating 1 net";
                    }
                    return `removed wiring, creating ${count} nets`;
                }
                // Check if this action only affects wiring (routeVertices)
                if (hasWiringOnlyChanges(actionRecord)) {
                    return "updated wiring";
                }
                // Check if this action affects PCB layout nodes (position/layout changes)
                // This handles cases where deleteSubjects is used for layout operations
                // without actually removing elements from the document
                const pcbLayoutChanges = extractPcbLayoutNodeChanges(actionRecord);
                if (pcbLayoutChanges.affectedNodeCount > 0) {
                    const count = pcbLayoutChanges.affectedNodeCount;
                    if (pcbLayoutChanges.hasRemovals && !pcbLayoutChanges.hasAdditions) {
                        return `removed ${count} PCB layout ${count === 1 ? "node" : "nodes"}`;
                    }
                    if (pcbLayoutChanges.hasAdditions && !pcbLayoutChanges.hasRemovals) {
                        return `added ${count} PCB layout ${count === 1 ? "node" : "nodes"}`;
                    }
                    return `updated PCB layout for ${count} ${count === 1 ? "component" : "components"}`;
                }
                // Check if this action updates element properties (replace operations)
                // This handles cases where deleteSubjects is used for element property updates,
                // not actual deletions (e.g., updating element positions or other properties)
                const elementUpdateChanges = extractElementUpdateChanges(actionRecord);
                if (elementUpdateChanges.hasUpdates) {
                    const count = elementUpdateChanges.updatedElementCount;
                    const componentWord = count === 1 ? "component" : "components";
                    // Provide more specific descriptions based on the type of change
                    switch (elementUpdateChanges.changeType) {
                        case "position":
                            return `changed ${count} ${componentWord} position`;
                        case "symbol_placement":
                            return `changed ${count} ${componentWord} symbol placement`;
                        case "property":
                            return `changed ${count} ${componentWord} properties`;
                        default:
                            return `updated ${count} ${componentWord}`;
                    }
                }
                return undefined;
            }
            // Get designators for elements and nets
            const elementDesignators = getDesignatorsForElements(removedElementUids, actionRecord, "remove");
            const netDesignators = getDesignatorsForNets(removedNetUids, actionRecord);
            // Case 1: Only elements were removed
            if (elementDesignators.length > 0 && netDesignators.length === 0 && removedNetUids.length === 0) {
                return `removed ${formatDesignatorList(elementDesignators)}`;
            }
            // Case 2: Only nets were removed (with labels)
            if (netDesignators.length > 0 && elementDesignators.length === 0 && removedElementUids.length === 0) {
                return `removed ${formatDesignatorList(netDesignators)}`;
            }
            // Case 3: Both elements and nets were removed (with labels)
            if (elementDesignators.length > 0 && netDesignators.length > 0) {
                return `removed ${formatDesignatorList(elementDesignators)} and ${formatDesignatorList(netDesignators)}`;
            }
            // Case 4: Elements with labels, nets without labels
            if (elementDesignators.length > 0 && removedNetUids.length > 0) {
                const netCount = removedNetUids.length;
                return `removed ${formatDesignatorList(elementDesignators)} and ${netCount} ${netCount === 1 ? "net" : "nets"}`;
            }
            // Case 5: Nets with labels, elements without labels
            if (netDesignators.length > 0 && removedElementUids.length > 0) {
                const elementCount = removedElementUids.length;
                return `removed ${elementCount} ${elementCount === 1 ? "component" : "components"} and ${formatDesignatorList(netDesignators)}`;
            }
            // Case 6: Only elements without labels
            if (removedElementUids.length > 0 && removedNetUids.length === 0) {
                const count = removedElementUids.length;
                return `removed ${count} ${count === 1 ? "component" : "components"}`;
            }
            // Case 7: Only nets without labels
            if (removedNetUids.length > 0 && removedElementUids.length === 0) {
                const count = removedNetUids.length;
                return `removed ${count} ${count === 1 ? "net" : "nets"}`;
            }
            // Case 8: Both elements and nets without labels
            if (removedElementUids.length > 0 && removedNetUids.length > 0) {
                const elementCount = removedElementUids.length;
                const netCount = removedNetUids.length;
                return `removed ${elementCount} ${elementCount === 1 ? "component" : "components"} and ${netCount} ${netCount === 1 ? "net" : "nets"}`;
            }
            return undefined;
        }
        case addElements.type: {
            const addedUids = extractAffectedElementUids(actionRecord, "add");
            if (addedUids.length === 0) {
                return undefined;
            }
            const designators = getDesignatorsForElements(addedUids, actionRecord, "add");
            if (designators.length === 0) {
                // Fall back to count-based description if no designators found
                const count = addedUids.length;
                return `added ${count} ${count === 1 ? "component" : "components"}`;
            }
            return `added ${formatDesignatorList(designators)}`;
        }
        case setSubjectProperties.type: {
            const subjectChanges = extractSubjectPropertyChanges(actionRecord, "set");
            if (subjectChanges.size === 0) {
                return undefined;
            }
            // Collect all property changes across all subjects
            const allChanges = [];
            for (const changes of subjectChanges.values()) {
                allChanges.push(...changes);
            }
            if (allChanges.length === 0) {
                const count = subjectChanges.size;
                return `updated properties on ${count} ${count === 1 ? "component" : "components"}`;
            }
            const propertyStr = formatPropertyChanges(allChanges);
            if (subjectChanges.size === 1) {
                return `set ${propertyStr}`;
            }
            return `set ${propertyStr} on ${subjectChanges.size} ${subjectChanges.size === 1 ? "component" : "components"}`;
        }
        case removeSubjectProperties.type: {
            const subjectChanges = extractSubjectPropertyChanges(actionRecord, "remove");
            if (subjectChanges.size === 0) {
                return undefined;
            }
            // Collect all property changes across all subjects
            const allChanges = [];
            for (const changes of subjectChanges.values()) {
                allChanges.push(...changes);
            }
            if (allChanges.length === 0) {
                const count = subjectChanges.size;
                return `removed properties from ${count} ${count === 1 ? "component" : "components"}`;
            }
            const propertyStr = formatPropertyChanges(allChanges, false);
            if (subjectChanges.size === 1) {
                return `removed ${propertyStr}`;
            }
            return `removed ${propertyStr} from ${subjectChanges.size} ${subjectChanges.size === 1 ? "component" : "components"}`;
        }
        case setDocumentProperties.type: {
            const changes = extractDocumentPropertyChanges(actionRecord, "set");
            if (changes.length === 0) {
                // Fallback: if no property changes but the action was triggered,
                // it might be an updated_at-only change
                if (isPatchBasedActionRecord(actionRecord)) {
                    const hasUpdatedAtChange = actionRecord.actionChangeDiff.forward.some((patch) => patch.path[0] === "updated_at");
                    if (hasUpdatedAtChange) {
                        return "updated project properties";
                    }
                }
                return undefined;
            }
            const propertyStr = formatPropertyChanges(changes);
            return `set project properties: ${propertyStr}`;
        }
        case removeDocumentProperties.type: {
            const changes = extractDocumentPropertyChanges(actionRecord, "remove");
            if (changes.length === 0) {
                return undefined;
            }
            const propertyStr = formatPropertyChanges(changes, false);
            return `removed project properties: ${propertyStr}`;
        }
        case updateDescription.type: {
            const descChange = extractDescriptionChange(actionRecord);
            if (descChange) {
                if (descChange.oldValue && descChange.newValue) {
                    return `updated project description from "${truncateText(descChange.oldValue)}" to "${truncateText(descChange.newValue)}"`;
                }
                if (descChange.newValue) {
                    return `set project description to "${truncateText(descChange.newValue)}"`;
                }
            }
            return "updated project description";
        }
        case FRONTEND_ACTION_NAMES.updateName: {
            const nameChange = extractNameChange(actionRecord);
            if (nameChange) {
                if (nameChange.oldValue && nameChange.newValue) {
                    return `updated project name from "${truncateText(nameChange.oldValue)}" to "${truncateText(nameChange.newValue)}"`;
                }
                if (nameChange.newValue) {
                    return `set project name to "${truncateText(nameChange.newValue)}"`;
                }
            }
            return "updated the project name";
        }
        case FRONTEND_ACTION_NAMES.addSubjectsFromClipboard: {
            const addedUids = extractAffectedElementUids(actionRecord, "add");
            if (addedUids.length === 0) {
                return undefined;
            }
            const designators = getDesignatorsForElements(addedUids, actionRecord, "add");
            if (designators.length === 0) {
                // Fall back to count-based description if no designators found
                const count = addedUids.length;
                return `pasted ${count} ${count === 1 ? "component" : "components"}`;
            }
            return `pasted ${formatDesignatorList(designators)}`;
        }
        case FRONTEND_ACTION_NAMES.setGlobalPcbRuleSet: {
            const changes = extractPcbRuleSetChanges(actionRecord);
            if (changes.length === 0) {
                return undefined;
            }
            // Build description based on what changed
            const descriptions = [];
            for (const change of changes) {
                if (change.isNew) {
                    // New rule set added - use "rule set" terminology
                    if (change.ruleSetName) {
                        descriptions.push(`added pcb layout rule set "${truncateText(change.ruleSetName)}"`);
                    }
                    else if (change.selector) {
                        descriptions.push(`added pcb layout rule set with selector "${truncateText(change.selector)}"`);
                    }
                    else {
                        descriptions.push("added a pcb layout rule set");
                    }
                }
                else {
                    // Existing rule set updated - individual rules within a rule set
                    if (change.selector && change.oldSelector) {
                        descriptions.push(`updated selector from "${truncateText(change.oldSelector)}" to "${truncateText(change.selector)}"`);
                    }
                    else if (change.selector) {
                        descriptions.push(`set selector to "${truncateText(change.selector)}"`);
                    }
                    // Rules added (individual rules within a rule set)
                    if (change.rulesAdded.length > 0) {
                        const ruleNames = change.rulesAdded.slice(0, MAX_PROPERTIES_TO_SHOW);
                        const remaining = change.rulesAdded.length - MAX_PROPERTIES_TO_SHOW;
                        let ruleStr = ruleNames.join(", ");
                        if (remaining > 0) {
                            ruleStr += `, and ${remaining} more`;
                        }
                        descriptions.push(`added ${ruleStr} rule${change.rulesAdded.length > 1 ? "s" : ""}`);
                    }
                    // Rules removed (individual rules within a rule set)
                    if (change.rulesRemoved.length > 0) {
                        const ruleNames = change.rulesRemoved.slice(0, MAX_PROPERTIES_TO_SHOW);
                        const remaining = change.rulesRemoved.length - MAX_PROPERTIES_TO_SHOW;
                        let ruleStr = ruleNames.join(", ");
                        if (remaining > 0) {
                            ruleStr += `, and ${remaining} more`;
                        }
                        descriptions.push(`removed ${ruleStr} rule${change.rulesRemoved.length > 1 ? "s" : ""}`);
                    }
                    // Rules updated (individual rules within a rule set)
                    if (change.rulesUpdated.length > 0) {
                        const updates = change.rulesUpdated.slice(0, MAX_PROPERTIES_TO_SHOW);
                        for (const update of updates) {
                            if (update.newValue !== undefined) {
                                descriptions.push(`set ${update.key} to ${formatPropertyValue(update.newValue)}`);
                            }
                        }
                        const remaining = change.rulesUpdated.length - MAX_PROPERTIES_TO_SHOW;
                        if (remaining > 0) {
                            descriptions.push(`updated ${remaining} more rule${remaining > 1 ? "s" : ""}`);
                        }
                    }
                }
            }
            if (descriptions.length === 0) {
                return "updated project pcb layout rule set";
            }
            // Join multiple descriptions
            if (descriptions.length === 1) {
                return descriptions[0];
            }
            return descriptions.slice(0, 3).join("; ");
        }
        case FRONTEND_ACTION_NAMES.removeGlobalPcbRuleSet: {
            // Extract rule set names from the reverse patches
            const ruleSetNames = extractRemovedPcbRuleSetNames(actionRecord);
            if (ruleSetNames.length > 0) {
                if (ruleSetNames.length === 1) {
                    return `removed pcb layout rule set '${truncateText(ruleSetNames[0], MAX_DESCRIPTION_LENGTH)}'`;
                }
                return `removed ${ruleSetNames.length} pcb layout rule sets`;
            }
            // Fall back to count if no names found
            const removedUids = extractRemovedPcbRuleSetUids(actionRecord);
            if (removedUids.length === 0) {
                return undefined;
            }
            const count = removedUids.length;
            return `removed ${count} pcb layout rule set${count > 1 ? "s" : ""}`;
        }
        case FRONTEND_ACTION_NAMES.toggleGlobalPcbRule: {
            const toggleChange = extractPcbRuleToggleChange(actionRecord);
            if (!toggleChange) {
                return undefined;
            }
            return toggleChange.enabled ? "enabled a pcb layout rule set" : "disabled a pcb layout rule set";
        }
        case FRONTEND_ACTION_NAMES.updateWiring: {
            const wiringChanges = extractWiringChanges(actionRecord);
            // If no changes detected at all, return undefined to use fallback
            if (!wiringChanges.hasRouteChanges && !wiringChanges.hasVertexChanges) {
                return undefined;
            }
            const { netChanges, hasVertexChanges } = wiringChanges;
            // If we have net/route changes with labels, show specific net info
            if (netChanges.length > 0) {
                const addedNets = netChanges.filter((c) => c.isNew);
                const updatedNets = netChanges.filter((c) => c.isUpdated && !c.isNew && !c.isRemoved);
                const removedNets = netChanges.filter((c) => c.isRemoved);
                // Net merge case: one net is removed and merged into another
                // When a wire connects two separate nets, one net is consumed into the other
                if (removedNets.length > 0 && addedNets.length === 0) {
                    const removedLabels = removedNets.filter((c) => c.label).map((c) => c.label);
                    const updatedLabels = updatedNets.filter((c) => c.label).map((c) => c.label);
                    // If we have both removed and updated net labels, show "merged X into Y"
                    if (removedLabels.length === 1 && updatedLabels.length === 1) {
                        return `merged ${removedLabels[0]} into ${updatedLabels[0]}`;
                    }
                    // If we only have the removed net label
                    if (removedLabels.length > 0) {
                        if (removedLabels.length === 1) {
                            return `merged ${removedLabels[0]} into another net`;
                        }
                        return `merged ${formatDesignatorList(removedLabels)} into another net`;
                    }
                    // Fallback: just show count
                    return `merged ${removedNets.length} net${removedNets.length > 1 ? "s" : ""}`;
                }
                const descriptions = [];
                if (addedNets.length > 0) {
                    const addedLabels = addedNets.filter((c) => c.label).map((c) => c.label);
                    if (addedLabels.length > 0) {
                        descriptions.push(`added net ${formatDesignatorList(addedLabels)}`);
                    }
                    else {
                        descriptions.push(`added ${addedNets.length} net${addedNets.length > 1 ? "s" : ""}`);
                    }
                }
                if (updatedNets.length > 0) {
                    const updatedLabels = updatedNets.filter((c) => c.label).map((c) => c.label);
                    if (updatedLabels.length > 0) {
                        descriptions.push(`updated net ${formatDesignatorList(updatedLabels)}`);
                    }
                    else {
                        descriptions.push(`updated ${updatedNets.length} net${updatedNets.length > 1 ? "s" : ""}`);
                    }
                }
                if (descriptions.length > 0) {
                    return descriptions.length === 1 ? descriptions[0] : descriptions.join("; ");
                }
            }
            // If only vertex changes (wire geometry), show a generic but informative message
            if (hasVertexChanges) {
                return "updated wiring";
            }
            return "updated nets";
        }
        case FRONTEND_ACTION_NAMES.rotateElements: {
            // For rotate/flip, elements are modified in place with "replace" operations
            // Use extractTransformElementDesignators to get labels from the patches
            const designators = extractTransformElementDesignators(actionRecord);
            if (designators.length > 0) {
                return `rotated ${formatDesignatorList(designators)}`;
            }
            // Fall back to count
            const count = countAffectedSubjects(actionRecord);
            if (count > 0) {
                return `rotated ${count} component${count > 1 ? "s" : ""}`;
            }
            return undefined;
        }
        case FRONTEND_ACTION_NAMES.flipElements: {
            const designators = extractTransformElementDesignators(actionRecord);
            if (designators.length > 0) {
                return `flipped ${formatDesignatorList(designators)}`;
            }
            const count = countAffectedSubjects(actionRecord);
            if (count > 0) {
                return `flipped ${count} component${count > 1 ? "s" : ""}`;
            }
            // Fallback: check for pcbLayoutNodes layer changes (when only pcbNodeRuleSet is updated)
            const flipLayerChanges = extractFlipLayerChanges(actionRecord);
            if (flipLayerChanges.nodeCount > 0) {
                if (flipLayerChanges.targetLayer) {
                    return `flipped ${flipLayerChanges.nodeCount} component${flipLayerChanges.nodeCount > 1 ? "s" : ""} to ${flipLayerChanges.targetLayer} layer`;
                }
                return `flipped ${flipLayerChanges.nodeCount} component${flipLayerChanges.nodeCount > 1 ? "s" : ""}`;
            }
            return undefined;
        }
        case FRONTEND_ACTION_NAMES.lockObjects: {
            // Try to determine if locking or unlocking based on the diff
            const isLocking = extractLockState(actionRecord);
            const action = isLocking ? "locked" : "unlocked";
            const designators = extractTransformElementDesignators(actionRecord);
            if (designators.length > 0) {
                return `${action} ${formatDesignatorList(designators)}`;
            }
            const count = countAffectedSubjects(actionRecord);
            if (count > 0) {
                return `${action} ${count} object${count > 1 ? "s" : ""}`;
            }
            return undefined;
        }
        case FRONTEND_ACTION_NAMES.setCode: {
            return "updated project code";
        }
        case FRONTEND_ACTION_NAMES.revertDocumentVersion: {
            return "reverted to a previous version";
        }
        case FRONTEND_ACTION_NAMES.restoreDocumentVersion: {
            return "restored a previous version";
        }
        case FRONTEND_ACTION_NAMES.convertSubjectsToNewPart: {
            // For convert to new part, elements are removed, so we look for "remove" type patches
            const elementUids = extractAffectedElementUids(actionRecord, "remove");
            const designators = getDesignatorsForElements(elementUids, actionRecord, "remove");
            if (designators.length > 0) {
                return `converted ${formatDesignatorList(designators)} to a new part`;
            }
            const count = elementUids.length > 0 ? elementUids.length : countAffectedSubjects(actionRecord);
            if (count > 0) {
                return `converted ${count} component${count > 1 ? "s" : ""} to a new part`;
            }
            return "converted components to a new part";
        }
        case FRONTEND_ACTION_NAMES.updateElementsSchematicPosition: {
            // For position updates, elements are modified in place with "replace" operations
            // Detect the type of transform (move, rotation, flip) from the patch paths
            const transformType = detectTransformType(actionRecord);
            const designators = extractTransformElementDesignators(actionRecord);
            const count = designators.length > 0 ? designators.length : countAffectedSubjects(actionRecord);
            // Choose the appropriate verb based on the transform type
            let verb;
            switch (transformType) {
                case "rotation":
                    verb = "rotated";
                    break;
                case "flip":
                    verb = "flipped";
                    break;
                case "move_and_rotate":
                    verb = "moved and rotated";
                    break;
                case "move":
                default:
                    verb = "moved";
                    break;
            }
            if (designators.length > 0) {
                return `${verb} ${formatDesignatorList(designators)}`;
            }
            if (count > 0) {
                return `${verb} ${count} component${count > 1 ? "s" : ""}`;
            }
            return undefined;
        }
        case FRONTEND_ACTION_NAMES.updateElementsLabel: {
            const labelChanges = extractElementLabelChanges(actionRecord);
            if (labelChanges.length === 1) {
                const change = labelChanges[0];
                if (change.oldLabel && change.newLabel) {
                    return `renamed ${change.oldLabel} to ${change.newLabel}`;
                }
                else if (change.newLabel) {
                    return `set component designator to ${change.newLabel}`;
                }
            }
            else if (labelChanges.length > 1) {
                const newLabels = labelChanges.map((c) => c.newLabel).filter((l) => l !== undefined);
                if (newLabels.length > 0) {
                    return `updated ${newLabels.length} component designator${newLabels.length > 1 ? "s" : ""}`;
                }
            }
            return undefined;
        }
        case FRONTEND_ACTION_NAMES.updateSimulationTimeStep: {
            const change = extractSimulationTimeStepChange(actionRecord);
            if (change) {
                if (change.oldValue !== undefined && change.newValue !== undefined) {
                    return `updated simulation time step from ${change.oldValue} to ${change.newValue}`;
                }
                else if (change.newValue !== undefined) {
                    return `set simulation time step to ${change.newValue}`;
                }
            }
            return undefined;
        }
        case FRONTEND_ACTION_NAMES.updateSimulationTimeStepUnit: {
            const change = extractSimulationTimeStepUnitChange(actionRecord);
            if (change) {
                if (change.oldValue !== undefined && change.newValue !== undefined) {
                    return `updated simulation time step unit from ${change.oldValue} to ${change.newValue}`;
                }
                else if (change.newValue !== undefined) {
                    return `set simulation time step unit to ${change.newValue}`;
                }
            }
            return undefined;
        }
        case FRONTEND_ACTION_NAMES.updatePropertyVisibility: {
            const change = extractPropertyVisibilityChange(actionRecord);
            if (change) {
                // Use property name if available, otherwise fall back to a generic description
                // (we avoid showing raw UUIDs to users)
                const propertyLabel = change.propertyName;
                const visibilityText = change.visibility ? "visible" : "hidden";
                if (propertyLabel) {
                    // We have a human-readable property name
                    if (change.elementCount > 1) {
                        return `set ${propertyLabel} ${visibilityText} on ${change.elementCount} components`;
                    }
                    return `set ${propertyLabel} ${visibilityText}`;
                }
                // Fall back to generic description when we don't have a property name
                if (change.elementCount > 1) {
                    return `changed property visibility on ${change.elementCount} components`;
                }
                return "changed property visibility";
            }
            return undefined;
        }
        case FRONTEND_ACTION_NAMES.addSubjectsPropertiesFromClipboard: {
            // Similar to setSubjectProperties but for pasted properties
            const subjectChanges = extractSubjectPropertyChanges(actionRecord, "set");
            if (subjectChanges.size === 0) {
                return undefined;
            }
            // Collect all property changes across all subjects
            const allChanges = [];
            for (const changes of subjectChanges.values()) {
                allChanges.push(...changes);
            }
            if (allChanges.length === 0) {
                const count = subjectChanges.size;
                return `pasted properties to ${count} ${count === 1 ? "component" : "components"}`;
            }
            const propertyStr = formatPropertyChanges(allChanges);
            if (subjectChanges.size === 1) {
                return `pasted ${propertyStr}`;
            }
            return `pasted ${propertyStr} to ${subjectChanges.size} ${subjectChanges.size === 1 ? "component" : "components"}`;
        }
        case FRONTEND_ACTION_NAMES.duplicateGlobalPcbRuleSet: {
            // For duplicating a PCB rule set, we look for added rule sets
            const changes = extractPcbRuleSetChanges(actionRecord);
            if (changes.length > 0) {
                // Find the first new rule set
                for (const change of changes) {
                    if (change.isNew && change.ruleSetName) {
                        return `duplicated pcb layout rule set '${truncateText(change.ruleSetName, MAX_DESCRIPTION_LENGTH)}'`;
                    }
                }
            }
            return "duplicated pcb layout rule set";
        }
        case FRONTEND_ACTION_NAMES.addGlobalRulesFromClipboard: {
            // For pasting PCB rule sets
            const changes = extractPcbRuleSetChanges(actionRecord);
            const newRuleSets = changes.filter((c) => c.isNew);
            if (newRuleSets.length > 0) {
                if (newRuleSets.length === 1 && newRuleSets[0]?.ruleSetName) {
                    return `pasted pcb layout rule set '${truncateText(newRuleSets[0].ruleSetName, MAX_DESCRIPTION_LENGTH)}'`;
                }
                return `pasted ${newRuleSets.length} pcb layout rule sets`;
            }
            return "pasted pcb layout rule sets";
        }
        case FRONTEND_ACTION_NAMES.setAssets: {
            const assetChanges = extractAssetChanges(actionRecord, "add");
            if (assetChanges.assetNames.length > 0) {
                if (assetChanges.assetNames.length === 1) {
                    return `added asset '${truncateText(assetChanges.assetNames[0], MAX_DESCRIPTION_LENGTH)}'`;
                }
                return `added ${assetChanges.assetCount} assets`;
            }
            else if (assetChanges.assetCount > 0) {
                return `added ${assetChanges.assetCount} asset${assetChanges.assetCount > 1 ? "s" : ""}`;
            }
            return undefined;
        }
        case FRONTEND_ACTION_NAMES.removeAssets: {
            const assetChanges = extractAssetChanges(actionRecord, "remove");
            if (assetChanges.assetNames.length > 0) {
                if (assetChanges.assetNames.length === 1) {
                    return `removed asset '${truncateText(assetChanges.assetNames[0], MAX_DESCRIPTION_LENGTH)}'`;
                }
                return `removed ${assetChanges.assetCount} assets`;
            }
            else if (assetChanges.assetCount > 0) {
                return `removed ${assetChanges.assetCount} asset${assetChanges.assetCount > 1 ? "s" : ""}`;
            }
            return undefined;
        }
        case FRONTEND_ACTION_NAMES.addNewElement: {
            // For adding a single new element
            const elementUids = extractAffectedElementUids(actionRecord, "add");
            const designators = getDesignatorsForElements(elementUids, actionRecord, "add");
            if (designators.length > 0) {
                return `added ${formatDesignatorList(designators)}`;
            }
            return "added a component";
        }
        case FRONTEND_ACTION_NAMES.addNewRoute: {
            // For adding a single new net/route
            const netUids = extractAffectedNetUids(actionRecord, "add");
            const designators = getDesignatorsForAddedNets(netUids, actionRecord);
            if (designators.length > 0) {
                return `added net ${formatDesignatorList(designators)}`;
            }
            return "added a net";
        }
        case FRONTEND_ACTION_NAMES.addRoutes: {
            // For adding multiple routes/nets
            const netUids = extractAffectedNetUids(actionRecord, "add");
            const designators = getDesignatorsForAddedNets(netUids, actionRecord);
            if (designators.length > 0) {
                return `added net${designators.length > 1 ? "s" : ""} ${formatDesignatorList(designators)}`;
            }
            if (netUids.length > 0) {
                return `added ${netUids.length} net${netUids.length > 1 ? "s" : ""}`;
            }
            return "added nets";
        }
        case FRONTEND_ACTION_NAMES.addBranchPoint: {
            // For adding a branch point (connection) to a net
            const netUids = extractAffectedNetUids(actionRecord, "add");
            const designators = getDesignatorsForAddedNets(netUids, actionRecord);
            if (designators.length > 0) {
                return `connected a wire to net ${formatDesignatorList(designators)}`;
            }
            return "connected a wire to a net";
        }
        case FRONTEND_ACTION_NAMES.updateElementsPosition: {
            // For moving elements (similar to updateElementsSchematicPosition)
            // Detect the type of transform (move, rotation, flip) from the patch paths
            const transformType = detectTransformType(actionRecord);
            const designators = extractTransformElementDesignators(actionRecord);
            const count = designators.length > 0 ? designators.length : countAffectedSubjects(actionRecord);
            // Choose the appropriate verb based on the transform type
            let verb;
            switch (transformType) {
                case "rotation":
                    verb = "rotated";
                    break;
                case "flip":
                    verb = "flipped";
                    break;
                case "move_and_rotate":
                    verb = "moved and rotated";
                    break;
                case "move":
                default:
                    verb = "moved";
                    break;
            }
            if (designators.length > 0) {
                return `${verb} ${formatDesignatorList(designators)}`;
            }
            if (count > 0) {
                return `${verb} ${count} component${count > 1 ? "s" : ""}`;
            }
            return undefined;
        }
        case updateNetsLabel.type: {
            // For updating net labels/designators
            const labelChanges = extractNetLabelChanges(actionRecord);
            if (labelChanges.length === 0) {
                return undefined;
            }
            // Single net label change - show old and new values
            if (labelChanges.length === 1) {
                const change = labelChanges[0];
                if (change !== undefined) {
                    if (change.oldLabel && change.newLabel) {
                        return `updated net designator from '${truncateText(change.oldLabel)}' to '${truncateText(change.newLabel)}'`;
                    }
                    if (change.newLabel) {
                        return `set net designator to '${truncateText(change.newLabel)}'`;
                    }
                }
            }
            // Multiple net label changes
            const newLabels = labelChanges.map((c) => c.newLabel).filter((l) => !!l);
            if (newLabels.length > 0) {
                return `updated ${newLabels.length} net designator${newLabels.length > 1 ? "s" : ""}`;
            }
            return `updated ${labelChanges.length} net designator${labelChanges.length > 1 ? "s" : ""}`;
        }
        case FRONTEND_ACTION_NAMES.removeNodes: {
            // For removing PCB layout nodes
            const nodeCount = countRemovedPcbNodes(actionRecord);
            if (nodeCount > 0) {
                return `removed ${nodeCount} PCB node${nodeCount > 1 ? "s" : ""}`;
            }
            return "removed PCB nodes";
        }
        case FRONTEND_ACTION_NAMES.setNodeRules: {
            // For setting PCB node rules
            const ruleChanges = extractNodeRuleChanges(actionRecord, "set");
            if (ruleChanges.ruleNames.length > 0) {
                if (ruleChanges.ruleNames.length === 1 && ruleChanges.ruleNames[0]) {
                    return `set ${ruleChanges.ruleNames[0]} layout rule`;
                }
                return `set ${ruleChanges.ruleNames.length} layout rule${ruleChanges.ruleNames.length > 1 ? "s" : ""}`;
            }
            if (ruleChanges.nodeCount > 0) {
                return `set layout rules on ${ruleChanges.nodeCount} node${ruleChanges.nodeCount > 1 ? "s" : ""}`;
            }
            return "set PCB layout rules";
        }
        case FRONTEND_ACTION_NAMES.removeNodeRules: {
            // For removing PCB node rules
            const ruleChanges = extractNodeRuleChanges(actionRecord, "remove");
            if (ruleChanges.ruleNames.length > 0) {
                if (ruleChanges.ruleNames.length === 1 && ruleChanges.ruleNames[0]) {
                    return `removed ${ruleChanges.ruleNames[0]} layout rule`;
                }
                return `removed ${ruleChanges.ruleNames.length} layout rule${ruleChanges.ruleNames.length > 1 ? "s" : ""}`;
            }
            if (ruleChanges.nodeCount > 0) {
                return `removed layout rules from ${ruleChanges.nodeCount} node${ruleChanges.nodeCount > 1 ? "s" : ""}`;
            }
            return "removed PCB layout rules";
        }
        case FRONTEND_ACTION_NAMES.toggleNodeSpecificRule: {
            // For toggling node-specific rules (enabled/disabled)
            const toggleState = extractNodeRuleToggleState(actionRecord);
            if (toggleState.ruleName) {
                return toggleState.disabled
                    ? `disabled ${toggleState.ruleName} layout rule`
                    : `enabled ${toggleState.ruleName} layout rule`;
            }
            return toggleState.disabled ? "disabled a layout rule" : "enabled a layout rule";
        }
        case FRONTEND_ACTION_NAMES.rewrittenProjectProperties: {
            // For AI-rewritten project properties
            const propertyChanges = extractRewrittenProjectPropertyChanges(actionRecord);
            if (propertyChanges.addedCount > 0 && propertyChanges.removedCount > 0) {
                return `updated ${propertyChanges.addedCount + propertyChanges.removedCount} project properties`;
            }
            if (propertyChanges.addedCount > 0) {
                return `set ${propertyChanges.addedCount} project propert${propertyChanges.addedCount > 1 ? "ies" : "y"}`;
            }
            if (propertyChanges.removedCount > 0) {
                return `removed ${propertyChanges.removedCount} project propert${propertyChanges.removedCount > 1 ? "ies" : "y"}`;
            }
            return "updated project properties";
        }
        case FRONTEND_ACTION_NAMES.rewrittenProjectDescription: {
            // For AI-rewritten project description
            const descChange = extractDescriptionChange(actionRecord);
            if (descChange) {
                if (descChange.newValue) {
                    return `updated project description to '${truncateText(descChange.newValue)}'`;
                }
            }
            return "updated project description";
        }
        case FRONTEND_ACTION_NAMES.undoDocument: {
            // For undo operations - use a generic description
            return "undid a change";
        }
        case FRONTEND_ACTION_NAMES.applyAutoLayoutIteration: {
            // For auto-layout iterations
            return "applied auto-layout routing";
        }
        case FRONTEND_ACTION_NAMES.createProjectDoc: {
            const info = extractMarkdownDocInfo(actionRecord);
            if (info) {
                return `created document '${truncateText(info.name, MAX_DESCRIPTION_LENGTH)}'`;
            }
            return "created a document";
        }
        case FRONTEND_ACTION_NAMES.updateProjectDoc: {
            const info = extractMarkdownDocInfo(actionRecord);
            if (info) {
                return `updated document '${truncateText(info.name, MAX_DESCRIPTION_LENGTH)}'`;
            }
            return "updated a document";
        }
        case FRONTEND_ACTION_NAMES.deleteProjectDoc: {
            const info = extractMarkdownDocInfo(actionRecord);
            if (info) {
                return `deleted document '${truncateText(info.name, MAX_DESCRIPTION_LENGTH)}'`;
            }
            return "deleted a document";
        }
        default:
            return undefined;
    }
}
//# sourceMappingURL=generateActionDescription.js.map